import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { downloadPdf as downloadPdfUtil, buildPdfUrl, openPdfInBrowser, printPdf } from '../utils/pdfUtils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Plus, Trash, PencilSimple, UserCircle, Users, ClockCounterClockwise, CurrencyEur, FilePdf, Eye, CaretDown, CaretUp, CaretRight, Phone, Buildings, House, OfficeChair, MagnifyingGlass, Tag, FunnelSimple, Broom, ArrowSquareOut, Lock, DownloadSimple, Printer, SortAscending, SortDescending, FileText, Archive, ArrowCounterClockwise, CalendarBlank } from '@phosphor-icons/react';
import { toast } from 'sonner';
import CleaningHistory from './CleaningHistory';
import DatePickerDMY from '../components/DatePickerDMY';
import SmartDateInput from '../components/SmartDateInput';
import DateModeSelector from '../components/DateModeSelector';
import { CleanerSelectorDropdown } from '../components/CleanerSelectorDropdown';
import { CleanerInvoicesTab, CleanerInvoiceDashboard } from '../components/CleanerInvoicesTab';
import { CleanerStatementTab } from '../components/CleanerStatementTab';
import { ClientJobsPanel } from '../components/ClientJobsPanel';
import { CleaningSchedule } from '../components/CleaningSchedule';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const axCfg = { withCredentials: true };
const fmtDate = (s) => { if (!s) return '-'; const p = s.split('-'); return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : s; };

// Phase 616 — Compact ledger description for Statement rows.
// Keeps the row scannable like a real finance ledger; full text remains
// accessible via the `title=` tooltip on the rendered <span>.
const ledgerLabel = (text, max = 60) => {
  const s = (text || '').replace(/\s+/g, ' ').trim();
  if (!s) return '';
  if (s.length <= max) return s;
  return s.slice(0, max - 1).trim() + '…';
};

// Phase 636 — Allocation status badge for cleaner work rows. Computes
// PAID / PARTIAL / UNPAID purely from the real ledger numbers (paid vs
// total) so the badge is always synchronized with the allocation
// engine — no manual flags, no separate truth.
//
// Returns { code, label, className } for use inside a <span>.
const allocStatus = (total, paid, t) => {
  const tot = Math.max(0, Number(total || 0));
  const pd = Math.max(0, Number(paid || 0));
  // Threshold-based: € 0.005 is below the rounding noise floor.
  if (tot <= 0.005) {
    return { code: 'na', label: '—', className: 'text-zinc-300' };
  }
  if (pd >= tot - 0.005) {
    return {
      code: 'paid',
      label: (t && t('statusPaid')) || 'PAID',
      className: 'inline-block px-1.5 py-0.5 text-[9px] font-semibold tracking-wider border border-emerald-200 bg-emerald-50 text-emerald-700',
    };
  }
  if (pd > 0.005) {
    return {
      code: 'partial',
      label: (t && t('statusPartial')) || 'PARTIAL',
      className: 'inline-block px-1.5 py-0.5 text-[9px] font-semibold tracking-wider border border-amber-200 bg-amber-50 text-amber-700',
    };
  }
  return {
    code: 'unpaid',
    label: (t && t('statusUnpaid')) || 'UNPAID',
    className: 'inline-block px-1.5 py-0.5 text-[9px] font-semibold tracking-wider border border-zinc-200 bg-zinc-50 text-zinc-500',
  };
};

// Phase 265 — Hours parser. Accepts either decimal hours (e.g. "4.75")
// OR HH:MM clock notation (e.g. "4:45" → 4 + 45/60 = 4.75). User report:
// the previous decimal-only `<input type="number">` made entries like
// "4:45" silently truncate to 4 (parseFloat stops at the colon),
// leading to wrong cleaner cost calculations.
//
// Returns a finite Number (>= 0) or 0 when the input is empty/invalid.
// Never returns NaN — callers can use the result directly in arithmetic.
const parseTimeToHours = (input) => {
  if (input == null) return 0;
  if (typeof input === 'number') return Number.isFinite(input) ? Math.max(0, input) : 0;
  const trimmed = String(input).trim().replace(',', '.');
  if (!trimmed) return 0;
  if (trimmed.includes(':')) {
    const [h, m, ...rest] = trimmed.split(':');
    if (rest.length > 0) return 0;
    const hh = parseInt(h, 10);
    const mm = parseInt(m, 10);
    if (Number.isNaN(hh) || Number.isNaN(mm) || mm < 0 || mm >= 60) return 0;
    return hh + mm / 60;
  }
  const v = parseFloat(trimmed);
  return Number.isFinite(v) && v >= 0 ? v : 0;
};

// Phase 477 — Top-nav simplified. `statement` was rarely used as a
// top-level view (it duplicated what's already in each client/cleaner
// detail) and `categories` is a client classification configuration
// that belongs INSIDE the Clients view. Both are removed from TABS;
// Categories management is now exposed via a small "Manage" button
// next to the client-type filter pills, opening the same CRUD UI in
// a dialog.
const TABS = ['cleaners', 'clients', 'jobHistory', 'cleaningHistory', 'cleaningSchedule'];

function CleaningManagementInner() {
  const { t, language } = useLanguage();
  useAuth();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(window.location.search);
  const initialTab = TABS.includes(searchParams.get('tab')) ? searchParams.get('tab') : 'cleaners';
  const [activeTab, setActiveTab] = useState(initialTab);
  const [cleaners, setCleaners] = useState([]);
  const [clients, setClients] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [buildingsList, setBuildingsList] = useState([]);

  // Cleaners state
  const [cleanerDialog, setCleanerDialog] = useState(false);
  const [editingCleaner, setEditingCleaner] = useState(null);
  const [cleanerForm, setCleanerForm] = useState({ name: '', phone: '', payment_type: 'hourly', hourly_rate: '', monthly_rate: '', notes: '' });
  const [deletingCleaner, setDeletingCleaner] = useState(null);
  const [expandedCleaner, setExpandedCleaner] = useState(null);
  const [cleanerPayments, setCleanerPayments] = useState([]);
  // Phase 735.43 — Legacy split/accordion mode retained as `'split'`
  // for downstream conditional guards; the toggle UI has been removed.
  // Kept declared to avoid touching unrelated code paths.
  // eslint-disable-next-line no-unused-vars
  const [cleanersViewMode, _setCleanersViewMode] = useState('split');
  // Phase 735.44 — Cleaner Invoices (Accounts-Payable). The dashboard
  // + tab-badge count get invalidated after every mutation via
  // `cleanerInvoicesRefreshKey`; per-cleaner counts are cached in
  // `cleanerInvoiceCounts` so the tab badge stays fresh without
  // re-fetching the entire invoice list on every render.
  const [cleanerInvoicesRefreshKey, setCleanerInvoicesRefreshKey] = useState(0);
  const [cleanerInvoiceCounts, setCleanerInvoiceCounts] = useState({});
  // Phase 735.44 — Whenever a cleaner is picked (or the invoice ledger
  // is mutated) refresh the badge count so the "Invoices (N)" tab tag
  // matches the live server state.
  const refreshCleanerInvoiceCount = useCallback(async (cid) => {
    if (!cid) return;
    try {
      const res = await axios.get(`${API}/cleaning/cleaners/${cid}/invoices?include_voided=false`, axCfg);
      setCleanerInvoiceCounts((prev) => ({ ...prev, [cid]: res.data?.invoices?.length ?? 0 }));
    } catch { /* silent */ }
  }, []);
  useEffect(() => {
    if (expandedCleaner) {
      refreshCleanerInvoiceCount(expandedCleaner);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandedCleaner, cleanerInvoicesRefreshKey]);
  // Phase 735.43 — Payment-status filter for the cleaner dropdown.
  // Persisted per-session in localStorage so the last-used chip
  // sticks between navigations.
  const [cleanerPaymentStatusFilter, setCleanerPaymentStatusFilter] = useState(() => {
    try {
      return window?.localStorage?.getItem('cm_cleaner_payment_status_filter') || 'all';
    } catch { return 'all'; }
  });
  useEffect(() => {
    try {
      window?.localStorage?.setItem('cm_cleaner_payment_status_filter', cleanerPaymentStatusFilter);
    } catch { /* ignore */ }
  }, [cleanerPaymentStatusFilter]);
  const [cleanerTabMemory, setCleanerTabMemory] = useState({}); // { cleaner_id: 'jobs'|... }
  const [cleanerStatements, setCleanerStatements] = useState({}); // { cleaner_id: statement }
  // Phase 642 — Separate cache for the UNFILTERED statement (always
  // full history). Used by the Ledger tab to compute Balance Brought
  // Forward when a period filter is active — the regular
  // `cleanerStatements` cache gets overwritten with date-filtered
  // results by `applyInlineStmtFilter`, so it can't see pre-FROM rows.
  const [cleanerStatementsAll, setCleanerStatementsAll] = useState({});
  // Phase 712 — Monthly payroll cache per cleaner. Built by the
  // Statement tab when first opened so the user sees the monthly
  // breakdown immediately. Always refetched when the universal
  // date filter is applied/cleared.
  const [cleanerMonthlyPayroll, setCleanerMonthlyPayroll] = useState({});
  const [monthlyPayrollLoading, setMonthlyPayrollLoading] = useState({});
  // Phase 712.3 — Track which grouped earnings rows are expanded.
  // Key shape: `${cleaner_id}|${month_key}|${date}`. We only ever store
  // expand state for grouped rows (single-entry dates render flat and
  // don't need state). Empty by default → everything collapsed.
  const [payrollGroupExpanded, setPayrollGroupExpanded] = useState({});
  const [isCleanerDetailLoading, setIsCleanerDetailLoading] = useState(false);
  const [cleanerViewMode, setCleanerViewMode] = useState('jobs'); // jobs | buildings | payments | statement
  // Phase 510 — "Add Building Cleaning" shortcut from the Cleaner profile.
  // We switch the parent tab to the embedded Building Cleaning view and
  // signal it to open its Add dialog with the current cleaner preselected.
  // A counter (not a boolean) lets the user press the button multiple times
  // even after closing the dialog without remounting the child.
  const [addBuildingCleaningSignal, setAddBuildingCleaningSignal] = useState(0);
  const [addBuildingCleaningPreset, setAddBuildingCleaningPreset] = useState(null);
  const [paymentDialog, setPaymentDialog] = useState(false);
  const [paymentForm, setPaymentForm] = useState({ amount: '', date: '', payroll_month: '', notes: '' });
  const [paymentCleanerId, setPaymentCleanerId] = useState(null);
  const [statementCleaner, setStatementCleaner] = useState(null);
  const [statementData, setStatementData] = useState(null);
  const [stmtViewMode, setStmtViewMode] = useState('all'); // 'all' | 'buildings' | 'jobs'
  const [expandedDates, setExpandedDates] = useState({});
  // Phase 602 — Cleaner status filter (active / inactive / all).
  // Default = active so the operational list stays focused; the user can
  // toggle to Inactive (archive) or All. Historical reports still
  // include archived cleaners — only the operational list filters them.
  const [cleanerStatusFilter, setCleanerStatusFilter] = useState('active');
  const [cleanerSearch, setCleanerSearch] = useState('');
  // Phase 263 — Bulk-delete selection state for Cleaners / Clients / Jobs.
  const [selectedCleanerIds, setSelectedCleanerIds] = useState(() => new Set());
  const [selectedClientIds, setSelectedClientIds] = useState(() => new Set());
  const [selectedJobIds, setSelectedJobIds] = useState(() => new Set());
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState(null); // {kind, ids, force}
  const [bulkDeleteResult, setBulkDeleteResult] = useState(null); // {kind, deleted, blocked}

  // Clients state
  const [clientDialog, setClientDialog] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [clientForm, setClientForm] = useState({ name: '', phone: '', client_type: 'office', building_id: '', address: '', notes: '' });
  const [deletingClient, setDeletingClient] = useState(null);
  const [expandedClient, setExpandedClient] = useState(null);
  const [clientHistory, setClientHistory] = useState(null);
  const [clientTypeFilter, setClientTypeFilter] = useState('all');
  // Phase 477 — Categories management dialog (moved from a separate
  // top-level tab into the Clients view).
  const [categoriesDialog, setCategoriesDialog] = useState(false);
  const [clientSearch, setClientSearch] = useState('');
  const [clientViewMode, setClientViewMode] = useState('jobs'); // Phase 367 — jobs (default) | invoices | payments | statement
  const [clientViewMonth, setClientViewMonth] = useState(new Date().getMonth() + 1);
  const [clientViewYear, setClientViewYear] = useState(new Date().getFullYear());
  // Phase 268 — Per-client Sales data fetched on demand when expanding a row.
  const [clientInvoices, setClientInvoices] = useState({});   // { client_id: [...] }
  const [clientStatements, setClientStatements] = useState({}); // { client_id: {...} }
  const [stmtFromDate, setStmtFromDate] = useState('');
  const [stmtToDate, setStmtToDate] = useState('');
  const [clientPayDialog, setClientPayDialog] = useState(false);
  const [clientPayForm, setClientPayForm] = useState({ amount: '', date: '', period_month: '', period_year: '', notes: '' });
  const [clientPayTarget, setClientPayTarget] = useState(null);
  // Phase 471 — Master/Detail split-view for Clients (replaces accordion).
  // Default to "split" on ≥1024px and "accordion" on smaller screens.
  // The user can switch between the two via a toolbar toggle.
  const [clientsViewMode, setClientsViewMode] = useState(() =>
    (typeof window !== 'undefined' && window.innerWidth >= 1024) ? 'split' : 'accordion'
  );
  // Remember the last sub-tab opened per client so switching clients
  // restores their last view (Jobs / Invoices / Payments / Statement).
  const [clientTabMemory, setClientTabMemory] = useState({}); // { client_id: 'jobs'|... }
  // Tracks whether the right-panel data (history + sales) is still loading
  // so we can show a skeleton instead of a flashing empty table.
  const [isClientDetailLoading, setIsClientDetailLoading] = useState(false);

  // Jobs state
  const [jobDialog, setJobDialog] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [jobCleanerRows, setJobCleanerRows] = useState([{ cleaner_id: '', hours_worked: '', cleaner_earned: '' }]);
  const [jobForm, setJobForm] = useState({ date: '', client_id: '', notes: '' });
  const [deletingJob, setDeletingJob] = useState(null);
  const [jobSearch, setJobSearch] = useState('');
  // Phase 365 — Unified Date Mode (Date / Month / Range / All).
  // Replaces the previous jobPeriod / jobMonth / jobYear triplet.
  const [jobDateMode, setJobDateMode] = useState('all');
  const [jobDateFrom, setJobDateFrom] = useState('');
  const [jobDateTo, setJobDateTo] = useState('');
  const [jobCleanerFilter, setJobCleanerFilter] = useState('all');
  const [jobClientFilter, setJobClientFilter] = useState('all');
  // Phase 365 — Manual-only is now permanent. The dropdown was removed per
  // user request: Job History should never display auto-generated rows
  // (source === 'cleaning_history_auto'). The filter is hardcoded in
  // filteredJobs below.

  // Categories state
  const [catDialog, setCatDialog] = useState(false);
  const [editingCat, setEditingCat] = useState(null);
  const [catForm, setCatForm] = useState({ name: '', name_el: '' });
  const [deletingCat, setDeletingCat] = useState(null);

  // Statement tab state
  const [stmtFrom, setStmtFrom] = useState(() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`; });
  const [stmtTo, setStmtTo] = useState(() => new Date().toISOString().split('T')[0]);
  const [stmtDateMode, setStmtDateMode] = useState('month');
  const [financialData, setFinancialData] = useState(null);
  const [stmtLoading, setStmtLoading] = useState(false);
  const [stmtClnrFrom, setStmtClnrFrom] = useState('');
  const [stmtClnrTo, setStmtClnrTo] = useState('');
  const [stmtClnrDateMode, setStmtClnrDateMode] = useState('all');
  const [stmtClnrClientFilter, setStmtClnrClientFilter] = useState('all');
  const [showBuildingsInPdf, setShowBuildingsInPdf] = useState(true);
  // Phase 366 — Sort order toggle (asc/desc) — default oldest-first.
  // Shared across Cleaner Statement, Job History and the Statement (financial) views.
  const [sortOrder, setSortOrder] = useState('asc');

  // Phase 507 — Bulk preload every cleaner's statement once on mount so the
  // sidebar list and the inline Statement tab always read from the SAME
  // source as the right-panel detail (the per-cleaner statement endpoint).
  // Previously the sidebar showed the stale `cleaners[*].remaining_balance`
  // returned by the listing endpoint, which didn't reflect new jobs/
  // payments until a full page refresh.
  //
  // Phase 639 — `cleanerStatusFilter` MUST be in the `useCallback` deps so
  // the closure picks up the latest value. Without it the Inactive /
  // All tabs would always refetch `?status=active` and look empty.
  const fetchCleaners = useCallback(async () => {
    try {
      const list = (await axios.get(`${API}/cleaning/cleaners?status=${cleanerStatusFilter}`, axCfg)).data;
      setCleaners(list);
      // Fire all statement fetches in parallel — best-effort, errors are
      // swallowed so a single failure can't break the sidebar.
      await Promise.allSettled(
        (list || []).map(c =>
          axios.get(`${API}/cleaning/cleaners/${c.cleaner_id}/statement`, axCfg).then(r => {
            setCleanerStatements(prev => ({ ...prev, [c.cleaner_id]: r.data || null }));
            setCleanerStatementsAll(prev => ({ ...prev, [c.cleaner_id]: r.data || null }));
          })
        )
      );
    } catch {}
  }, [cleanerStatusFilter]);
  const fetchClients = useCallback(async () => { try { setClients((await axios.get(`${API}/cleaning/clients`, axCfg)).data); } catch {} }, []);
  const fetchJobs = useCallback(async () => { try { setJobs((await axios.get(`${API}/cleaning/jobs`, axCfg)).data); } catch {} }, []);
  const fetchBuildings = useCallback(async () => { try { setBuildingsList((await axios.get(`${API}/buildings`, axCfg)).data); } catch {} }, []);
  const fetchCategories = useCallback(async () => { try { setCategories((await axios.get(`${API}/cleaning/categories`, axCfg)).data); } catch {} }, []);

  useEffect(() => { fetchCleaners(); }, [fetchCleaners, cleanerStatusFilter]);
  useEffect(() => { fetchClients(); fetchJobs(); fetchBuildings(); fetchCategories(); }, [fetchClients, fetchJobs, fetchBuildings, fetchCategories]);

  // === CLEANER CRUD ===
  const openCleanerDialog = (c = null) => {
    setEditingCleaner(c);
    setCleanerForm(c ? { name: c.name, phone: c.phone || '', payment_type: c.payment_type || 'hourly', hourly_rate: c.hourly_rate || '', monthly_rate: c.monthly_rate || '', notes: c.notes || '' } : { name: '', phone: '', payment_type: 'hourly', hourly_rate: '', monthly_rate: '', notes: '' });
    setCleanerDialog(true);
  };
  const saveCleaner = async () => { try { if (editingCleaner) await axios.put(`${API}/cleaning/cleaners/${editingCleaner.cleaner_id}`, cleanerForm, axCfg); else await axios.post(`${API}/cleaning/cleaners`, cleanerForm, axCfg); toast.success(t('saved')); setCleanerDialog(false); fetchCleaners(); } catch (e) { toast.error(e.response?.data?.detail || 'Error'); } };

  // Phase 606 — Per-cleaner Active/Inactive toggle.
  // Flips the real `status` field in the DB via the existing
  // /archive and /reactivate endpoints (Phase 602). After the
  // toggle we re-fetch the cleaner list (which is filtered by
  // `cleanerStatusFilter`) so inactive cleaners disappear from the
  // Active tab and reappear in the Inactive tab automatically.
  // The selected cleaner detail is reloaded too so the header
  // badge / disabled actions update in place without a manual
  // refresh.
  //
  // Phase 607 — Confirmation uses an inline <AlertDialog> instead of
  // window.confirm() because the native confirm is silently blocked
  // inside the Capacitor mobile wrapper and on some desktop browsers,
  // which made the previous click do nothing.
  const [cleanerStatusBusy, setCleanerStatusBusy] = useState(false);
  const [pendingStatusCleaner, setPendingStatusCleaner] = useState(null); // cleaner record awaiting confirmation
  const requestToggleCleanerStatus = (cleaner) => {
    if (!cleaner?.cleaner_id) return;
    setPendingStatusCleaner(cleaner);
  };
  const confirmToggleCleanerStatus = async () => {
    const cleaner = pendingStatusCleaner;
    if (!cleaner?.cleaner_id) {
      setPendingStatusCleaner(null);
      return;
    }
    const isInactive = (cleaner.status || 'active') === 'inactive';
    const url = isInactive ? 'reactivate' : 'archive';
    try {
      setCleanerStatusBusy(true);
      await axios.post(`${API}/cleaning/cleaners/${cleaner.cleaner_id}/${url}`, {}, axCfg);
      toast.success(isInactive ? (t('cleanerReactivated') || 'Cleaner reactivated') : (t('cleanerMarkedInactive') || 'Cleaner marked inactive'));
      setPendingStatusCleaner(null);
      // Refresh list — current tab filter will move the cleaner accordingly.
      await fetchCleaners();
      // Re-fetch the selected cleaner detail so the header reflects the new status.
      if (expandedCleaner === cleaner.cleaner_id) {
        await refreshCleanerDetail(cleaner.cleaner_id);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error');
    } finally {
      setCleanerStatusBusy(false);
    }
  };

  // Phase 263 — Single-delete now respects the linked-data 409 response.
  // The error payload arrives as `{detail: {message, linked_jobs, ...}}`.
  const deleteCleaner = async () => {
    try {
      await axios.delete(`${API}/cleaning/cleaners/${deletingCleaner.cleaner_id}`, axCfg);
      toast.success(t('deleted'));
      setDeletingCleaner(null);
      fetchCleaners();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && typeof detail === 'object' && detail?.linked_jobs) {
        toast.error(`${t('cannotDeleteLinked') || 'Cannot delete'} — ${detail.linked_jobs} ${t('jobs')}.`);
      } else {
        toast.error(typeof detail === 'string' ? detail : (detail?.message || 'Error'));
      }
    }
  };

  // Phase 263 — Bulk selection helpers (cleaners / clients / jobs).
  const toggleSelected = (setter) => (id) => {
    setter((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleSelectedCleaner = toggleSelected(setSelectedCleanerIds);
  const toggleSelectedClient = toggleSelected(setSelectedClientIds);
  const toggleSelectedJob = toggleSelected(setSelectedJobIds);
  const clearSelection = (kind) => {
    if (kind === 'cleaner') setSelectedCleanerIds(new Set());
    else if (kind === 'client') setSelectedClientIds(new Set());
    else if (kind === 'job') setSelectedJobIds(new Set());
  };
  const setAllSelected = (kind, items) => {
    const ids = items.map((x) => x.cleaner_id || x.client_id || x.job_id || x._flat_id).filter(Boolean);
    if (kind === 'cleaner') setSelectedCleanerIds(new Set(ids));
    else if (kind === 'client') setSelectedClientIds(new Set(ids));
    else if (kind === 'job') setSelectedJobIds(new Set(ids));
  };
  const performBulkDelete = async (force = false) => {
    const conf = bulkDeleteConfirm;
    if (!conf) return;
    try {
      const url = `${API}/cleaning/${conf.kind === 'cleaner' ? 'cleaners' : conf.kind === 'client' ? 'clients' : 'jobs'}/bulk-delete`;
      const body = conf.kind === 'cleaner' ? { cleaner_ids: conf.ids, force }
                 : conf.kind === 'client' ? { client_ids: conf.ids, force }
                 : { job_ids: conf.ids };
      const res = await axios.post(url, body, axCfg);
      const { deleted = [], blocked = [] } = res.data || {};
      setBulkDeleteConfirm(null);
      clearSelection(conf.kind);
      if (conf.kind === 'cleaner') fetchCleaners();
      if (conf.kind === 'client') fetchClients();
      fetchJobs();
      if (blocked.length > 0) {
        setBulkDeleteResult({ kind: conf.kind, deleted, blocked });
      } else {
        toast.success(`${deleted.length} ${t('deleted')}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    }
  };

  // === CLEANER PAYMENTS ===
  const toggleCleanerExpand = async (c) => {
    if (expandedCleaner === c.cleaner_id) { setExpandedCleaner(null); return; }
    selectCleaner(c);
  };

  // Phase 473 — Pull the full cleaner detail (statement endpoint already
  // returns jobs + buildings + payments in one consistent source) so the
  // right panel never disagrees with the left list totals.
  const fetchCleanerStatement = async (cleanerId) => {
    try {
      const r = await axios.get(`${API}/cleaning/cleaners/${cleanerId}/statement`, axCfg);
      setCleanerStatements(prev => ({ ...prev, [cleanerId]: r.data || null }));
      // Phase 642b — Also refresh the unfiltered cache so Balance
      // Brought Forward stays accurate after a manual refresh.
      setCleanerStatementsAll(prev => ({ ...prev, [cleanerId]: r.data || null }));
      return r.data;
    } catch (e) {
      console.error('[CleaningManagement] fetchCleanerStatement failed', e);
      return null;
    }
  };

  // Phase 514 — Per-cleaner date range filters for the inline Statement
  // tab. Each cleaner keeps its own From / To so a user can drill into one
  // cleaner without losing another's filter. `applied` mirrors the values
  // the backend statement was last fetched with, so the UI can show the
  // currently-active range in the PDF and the period chip.
  const [inlineStmtFilters, setInlineStmtFilters] = useState({}); // { cid: {from, to, applied:{from,to}} }
  const getInlineFilter = (cid) => inlineStmtFilters[cid] || { from: '', to: '', applied: { from: '', to: '' } };
  const setInlineFilter = (cid, patch) => setInlineStmtFilters(prev => ({ ...prev, [cid]: { ...getInlineFilter(cid), ...patch } }));

  const applyInlineStmtFilter = async (cid, override = null) => {
    // Phase 712.1 — accept an optional `override` { from, to } so the
    // caller can apply immediately after computing a Month/Year window
    // (sidesteps React's async setState batching).
    const f = getInlineFilter(cid);
    const from = override?.from ?? f.from;
    const to = override?.to ?? f.to;
    try {
      const params = new URLSearchParams();
      if (from) params.append('date_from', from);
      if (to) params.append('date_to', to);
      const qs = params.toString() ? `?${params.toString()}` : '';
      const r = await axios.get(`${API}/cleaning/cleaners/${cid}/statement${qs}`, axCfg);
      setCleanerStatements(prev => ({ ...prev, [cid]: r.data || null }));
      setInlineFilter(cid, { applied: { from, to } });
      // Refresh monthly payroll. When a month-aligned range is active,
      // pass `month=YYYY-MM` so the endpoint returns the single-month view.
      const monthParam = (from && from.slice(0, 7) === (to || '').slice(0, 7)) ? from.slice(0, 7) : '';
      fetchMonthlyPayroll(cid, { month: monthParam });
    } catch (e) {
      console.error('[CleaningManagement] applyInlineStmtFilter failed', e);
    }
  };

  const clearInlineStmtFilter = async (cid) => {
    setInlineFilter(cid, { from: '', to: '', draftMonth: '', draftYear: '', applied: { from: '', to: '' } });
    // Refetch unfiltered so summary cards + ledger reset immediately.
    await fetchCleanerStatement(cid);
    fetchMonthlyPayroll(cid, { month: '' });
  };

  // Phase 712 — Monthly payroll fetcher. Single-month mode when `month`
  // is provided (YYYY-MM); otherwise returns all months that have data
  // (for the Month/Year picker).
  const fetchMonthlyPayroll = useCallback(async (cid, opts) => {
    if (!cid) return;
    setMonthlyPayrollLoading(prev => ({ ...prev, [cid]: true }));
    try {
      const params = new URLSearchParams();
      const month = opts?.month;
      if (month) params.append('month', month);
      const qs = params.toString() ? `?${params.toString()}` : '';
      const r = await axios.get(`${API}/cleaning/cleaners/${cid}/monthly-payroll${qs}`, axCfg);
      setCleanerMonthlyPayroll(prev => ({ ...prev, [cid]: r.data || null }));
    } catch (e) {
      console.error('[CleaningManagement] fetchMonthlyPayroll failed', e);
    } finally {
      setMonthlyPayrollLoading(prev => ({ ...prev, [cid]: false }));
    }
  }, []);

  const selectCleaner = async (c) => {
    setExpandedCleaner(c.cleaner_id);
    setCleanerViewMode(cleanerTabMemory[c.cleaner_id] || 'jobs');
    setIsCleanerDetailLoading(true);
    try {
      // Fetch statement (jobs + buildings + payments) AND payments list in
      // parallel — payments endpoint is kept for backwards compat with
      // savePayment / deleteClnrPayment.
      const [stmt] = await Promise.all([
        fetchCleanerStatement(c.cleaner_id),
        axios.get(`${API}/cleaning/cleaners/${c.cleaner_id}/payments`, axCfg)
          .then(r => setCleanerPayments(r.data || []))
          .catch(() => setCleanerPayments([])),
      ]);
      return stmt;
    } finally {
      setIsCleanerDetailLoading(false);
    }
  };

  const setCleanerTab = (cleanerId, tabKey) => {
    setCleanerViewMode(tabKey);
    if (cleanerId) {
      setCleanerTabMemory(prev => ({ ...prev, [cleanerId]: tabKey }));
      // Phase 617 — Refetch on every money-screen tab focus so any edit
      // made in Finance/Sales is picked up immediately. Same fix as the
      // client side. Statement endpoint is live-derived; this just
      // invalidates the React-state cache so the user sees today's data.
      if (['payments', 'statement', 'jobs', 'buildings'].includes(tabKey)) {
        refreshCleanerDetail(cleanerId);
      }
      // Phase 712 — Lazy-load the Monthly Payroll dataset on first
      // focus of the Statement tab so we don't fetch it for users
      // who never visit the tab.
      if (tabKey === 'statement') {
        const flt = getInlineFilter(cleanerId);
        const from = flt.applied.from || '';
        const to = flt.applied.to || '';
        const month = (from && from.slice(0, 7) === to.slice(0, 7)) ? from.slice(0, 7) : '';
        fetchMonthlyPayroll(cleanerId, { month });
      }
    }
  };

  const refreshCleanerDetail = (cleanerId) => {
    if (!cleanerId) return;
    fetchCleanerStatement(cleanerId);
    axios.get(`${API}/cleaning/cleaners/${cleanerId}/payments`, axCfg)
      .then(r => setCleanerPayments(r.data || [])).catch(() => {});
  };
  // Phase 609 / 611 — Cleaner Payment with Allocations.
  // The payment modal now fetches ALL outstanding cleaner-owed debits
  // (jobs + buildings) from the shared LedgerService and lets the user
  // split the payment across any combination. State shape mirrors the
  // Bills allocation flow.
  const [paymentOpenJobs, setPaymentOpenJobs] = useState([]);         // open debits (jobs + buildings, single flat list)
  const [paymentAllocations, setPaymentAllocations] = useState({});   // {charge_id: amount}
  const [paymentAutoAllocate, setPaymentAutoAllocate] = useState(true);
  const [paymentEditingId, setPaymentEditingId] = useState(null);     // null = creating new, otherwise editing this payment_id
  // Phase 633 — Pending vs applied period filter inside the modal.
  // `Draft*` holds the inputs the user is currently typing; `paymentDateFrom/To`
  // is the committed filter that actually scopes the displayed debits.
  // Applying empty values is allowed (= no bound).
  const [paymentDateFrom, setPaymentDateFrom] = useState('');
  const [paymentDateTo, setPaymentDateTo] = useState('');
  const [paymentDateFromDraft, setPaymentDateFromDraft] = useState('');
  const [paymentDateToDraft, setPaymentDateToDraft] = useState('');

  // Phase 634 — Fetch open debits from backend scoped by the period
  // filter active inside the modal. Falls back to existing-payment
  // allocations (passed via `existingPaymentDetail`) when re-allocating
  // so rows already paid by this payment still appear with their full
  // metadata (name/date/total). Backend now enriches hydrated
  // allocations with `client_name` / `building_name` / `date` so the
  // synthetic merged rows render with proper labels instead of "-".
  const fetchPaymentDebits = async (cleanerId, fromIso, toIso, existingPaymentDetail = null) => {
    if (!cleanerId) return;
    try {
      const params = new URLSearchParams();
      if (fromIso) params.append('date_from', fromIso);
      if (toIso) params.append('date_to', toIso);
      const qs = params.toString() ? `?${params.toString()}` : '';
      const debitsR = await axios.get(`${API}/cleaning/cleaners/${cleanerId}/open-debits${qs}`, axCfg);
      const openDebits = Array.isArray(debitsR.data) ? debitsR.data : [];
      let mergedDebits = openDebits;
      const preset = {};
      if (existingPaymentDetail) {
        const allocs = existingPaymentDetail.allocations || [];
        const byCharge = new Map(openDebits.map((d) => [d.charge_id, d]));
        allocs.forEach((a) => {
          preset[a.charge_id] = a.amount;
          const existing = byCharge.get(a.charge_id);
          if (existing) {
            existing.remaining = Math.round((existing.remaining + a.amount + Number.EPSILON) * 100) / 100;
          } else {
            // Charge isn't in the filtered open list (either fully paid
            // by THIS payment, or outside the active date range). Build
            // a synthetic row from the enriched allocation payload so
            // the user can still see / adjust it.
            const isBuilding = (a.kind || (a.charge_id || '').startsWith('hist:') ? 'building' : 'job') === 'building';
            mergedDebits = [
              ...mergedDebits,
              {
                kind: a.kind || ((a.charge_id || '').startsWith('hist:') ? 'building' : 'job'),
                charge_id: a.charge_id,
                reference: a.charge_id,
                date: a.date || '',
                total: a.charge_total || a.amount,
                paid_amount: 0,
                remaining: a.amount,
                client_id: a.client_id || null,
                client_name: a.client_name || '',
                building_id: a.building_id || null,
                building_name: a.building_name || '',
              },
            ];
            // (isBuilding intentionally unused — kind already encoded above)
            void isBuilding;
          }
        });
      }
      setPaymentOpenJobs(mergedDebits);
      setPaymentAllocations(preset);
    } catch (e) {
      console.error('[CleaningManagement] fetchPaymentDebits failed', e);
      setPaymentOpenJobs([]);
    }
  };

  const openPaymentDialog = async (cleanerId, existingPayment = null) => {
    setPaymentCleanerId(cleanerId);
    setPaymentEditingId(existingPayment ? existingPayment.payment_id : null);
    // Phase 712.1 — Default the payroll_month to the cleaner-page's
    // currently-selected month (when the universal filter is on) so a
    // user viewing May's payroll has May pre-selected. Otherwise
    // default to the payment-date month, or the current month.
    const inherited = (inlineStmtFilters[cleanerId] || {}).applied || { from: '', to: '' };
    const inheritedMonth = (inherited.from || '').slice(0, 7);
    const todayMonth = new Date().toISOString().slice(0, 7);
    const defaultDate = existingPayment ? (existingPayment.date || new Date().toISOString().split('T')[0]) : new Date().toISOString().split('T')[0];
    const defaultPayrollMonth = existingPayment
      ? (existingPayment.payroll_month || (existingPayment.date || '').slice(0, 7) || todayMonth)
      : (inheritedMonth || defaultDate.slice(0, 7) || todayMonth);
    setPaymentForm({
      amount: existingPayment ? String(existingPayment.amount || '') : '',
      date: defaultDate,
      payroll_month: defaultPayrollMonth,
      notes: existingPayment ? (existingPayment.notes || '') : '',
    });
    setPaymentDialog(true);
  };

  const paymentTotals = useMemo(() => {
    const amount = parseFloat(paymentForm.amount) || 0;
    const allocated = Object.values(paymentAllocations).reduce((s, v) => s + (parseFloat(v) || 0), 0);
    const r2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;
    return { amount: r2(amount), allocated: r2(allocated), remaining: r2(amount - allocated) };
  }, [paymentForm.amount, paymentAllocations]);

  // Phase 633/634 — Group open debits by kind for the modal UI. The
  // backend now does the date filtering when fetching `/open-debits`, so
  // we only need to bucket + sort here. FIFO sort (oldest first,
  // charge_id stable tie-breaker) per group keeps each section reading
  // like a proper accounting ledger.
  //
  // Synthetic rows merged from an existing payment's allocations are also
  // filtered against the active range — per user directive: "NO entries
  // outside filter". The original allocation amounts are preserved in
  // `paymentAllocations` state regardless of visibility, so the save
  // payload still includes them and balances stay correct. To surface
  // any allocations that the active filter is hiding we also expose
  // `paymentOutOfRangeAllocs` (used for a small warning strip in the UI).
  const paymentDebitsByKind = useMemo(() => {
    const groups = { job: [], building: [] };
    const from = (paymentDateFrom || '').trim();
    const to = (paymentDateTo || '').trim();
    (paymentOpenJobs || []).forEach((d) => {
      const dt = d.date || '';
      if (from && dt && dt < from) return;
      if (to && dt && dt > to) return;
      // Rows with no date (extreme legacy edge case) only show when no
      // filter is active.
      if ((from || to) && !dt) return;
      const k = d.kind === 'building' ? 'building' : 'job';
      groups[k].push(d);
    });
    const sortFifo = (a, b) => {
      const da = a.date || '';
      const db = b.date || '';
      if (da !== db) return da < db ? -1 : 1;
      return String(a.charge_id || '').localeCompare(String(b.charge_id || ''));
    };
    groups.job.sort(sortFifo);
    groups.building.sort(sortFifo);
    return groups;
  }, [paymentOpenJobs, paymentDateFrom, paymentDateTo]);

  const paymentVisibleCount = paymentDebitsByKind.job.length + paymentDebitsByKind.building.length;
  // Phase 635 — Header count reflects the deduped view so the user sees
  // the same number of rows that are actually rendered.
  const paymentVisibleMergedCount = paymentDebitsByKind.job.length + (() => {
    // Same dedup math as paymentMergedBuildings but cheap (no memo
    // dependency cycle) — count unique (building_id, date) keys.
    const seen = new Set();
    (paymentDebitsByKind.building || []).forEach(r => {
      seen.add(`${r.building_id || '_'}|${r.date || '_'}`);
    });
    return seen.size;
  })();

  // Allocations whose underlying charge falls outside the active filter
  // but are still preserved in state. Surfaced as a warning strip so the
  // user knows their save will still write those allocations.
  const paymentOutOfRangeAllocs = useMemo(() => {
    const from = (paymentDateFrom || '').trim();
    const to = (paymentDateTo || '').trim();
    if (!from && !to) return [];
    // Visible underlying ids = jobs + every merged-building's underlying.
    const visibleIds = new Set(paymentDebitsByKind.job.map(r => r.charge_id));
    (paymentDebitsByKind.building || []).forEach(r => visibleIds.add(r.charge_id));
    const out = [];
    Object.entries(paymentAllocations).forEach(([cid, v]) => {
      const amt = parseFloat(v) || 0;
      if (amt <= 0) return;
      if (visibleIds.has(cid)) return;
      out.push({ charge_id: cid, amount: amt });
    });
    return out;
  }, [paymentAllocations, paymentDebitsByKind, paymentDateFrom, paymentDateTo]);
  const paymentOutOfRangeTotal = paymentOutOfRangeAllocs.reduce((s, a) => s + a.amount, 0);

  // Phase 635 — Dedupe building rows that share the same
  // (building_id, date). The underlying `cleaning_history` collection
  // has historically allowed double-inserts (millisecond-apart copies),
  // so showing them as separate rows in the modal misleads payroll
  // users into thinking the cleaner is owed twice. We GROUP them into
  // a single visible row whose `total / paid_amount / remaining` are
  // the sums of the underlying entries, while keeping the underlying
  // `charge_id`s in `underlying[]` so the save path still writes
  // allocations against individual `cleaning_history` documents — the
  // AllocationService and FIFO engine are untouched.
  //
  // JOBS are NOT merged: each (job_id, cleaner_id) is already unique
  // and a cleaner legitimately can have multiple jobs for the same
  // client on the same day.
  const round2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;
  const paymentMergedJobs = paymentDebitsByKind.job;
  const paymentMergedBuildings = useMemo(() => {
    const groups = new Map();
    const order = [];
    (paymentDebitsByKind.building || []).forEach((row) => {
      const key = `${row.building_id || '_'}|${row.date || '_'}`;
      if (!groups.has(key)) {
        groups.set(key, {
          ...row,
          underlying: [{ charge_id: row.charge_id, remaining: row.remaining || 0 }],
          // Use the first row's charge_id as the visible key — clicking it
          // will be intercepted to fan out to underlying entries.
          display_key: key,
          total: row.total || 0,
          paid_amount: row.paid_amount || 0,
          remaining: row.remaining || 0,
        });
        order.push(key);
      } else {
        const m = groups.get(key);
        m.underlying.push({ charge_id: row.charge_id, remaining: row.remaining || 0 });
        m.total = round2(m.total + (row.total || 0));
        m.paid_amount = round2(m.paid_amount + (row.paid_amount || 0));
        m.remaining = round2(m.remaining + (row.remaining || 0));
      }
    });
    return order.map(k => groups.get(k));
  }, [paymentDebitsByKind.building]);

  // Sum the per-entry allocations for a merged row so the OUTSTANDING
  // pill / input reflect the user's intent across all underlying entries.
  const sumMergedAlloc = (row) => {
    if (!row || !row.underlying) return parseFloat(paymentAllocations[row?.charge_id] || 0) || 0;
    let total = 0;
    row.underlying.forEach(u => {
      total += parseFloat(paymentAllocations[u.charge_id]) || 0;
    });
    return round2(total);
  };

  // Distribute a single amount across a merged row's underlying entries
  // using FIFO (cap each by the underlying entry's `remaining`). Returns
  // the resulting per-entry allocation map (for the row only).
  const distributeAcrossUnderlying = (row, amount) => {
    const out = {};
    let left = round2(Math.max(0, amount || 0));
    (row.underlying || []).forEach(u => {
      if (left <= 0.005) { out[u.charge_id] = ''; return; }
      const cap = round2(u.remaining || 0);
      const give = round2(Math.min(cap, left));
      out[u.charge_id] = give > 0 ? give.toFixed(2) : '';
      left = round2(left - give);
    });
    return out;
  };

  // Phase 634 — Apply / clear handlers now refetch from the backend with
  // date_from / date_to so the totals reported in the modal MATCH what
  // the cleaner-page tabs (Jobs / Buildings / Statement) show. Existing
  // allocations are preserved as user-entered values so swapping the
  // filter doesn't wipe pending edits — the preset re-merge logic only
  // runs at modal open time. If editing a payment, we also re-include
  // its existing allocations across filter changes by re-fetching the
  // payment detail and re-merging.
  const applyPaymentDateFilter = async () => {
    const fromIso = paymentDateFromDraft || '';
    const toIso = paymentDateToDraft || '';
    setPaymentDateFrom(fromIso);
    setPaymentDateTo(toIso);
    // Snapshot user-entered allocations so we can restore them after the
    // refetch overwrites paymentAllocations from the preset.
    const userAllocs = { ...paymentAllocations };
    let existingDetail = null;
    if (paymentEditingId && paymentCleanerId) {
      try {
        const r = await axios.get(`${API}/cleaning/cleaners/${paymentCleanerId}/payments/${paymentEditingId}`, axCfg);
        existingDetail = r.data || null;
      } catch (e) {
        existingDetail = null;
      }
    }
    await fetchPaymentDebits(paymentCleanerId, fromIso, toIso, existingDetail);
    setPaymentAllocations(userAllocs);
  };
  const clearPaymentDateFilter = async () => {
    setPaymentDateFrom(''); setPaymentDateTo('');
    setPaymentDateFromDraft(''); setPaymentDateToDraft('');
    const userAllocs = { ...paymentAllocations };
    let existingDetail = null;
    if (paymentEditingId && paymentCleanerId) {
      try {
        const r = await axios.get(`${API}/cleaning/cleaners/${paymentCleanerId}/payments/${paymentEditingId}`, axCfg);
        existingDetail = r.data || null;
      } catch (e) {
        existingDetail = null;
      }
    }
    await fetchPaymentDebits(paymentCleanerId, '', '', existingDetail);
    setPaymentAllocations(userAllocs);
  };

  // Phase 632 — Click an outstanding amount cell to autofill the row's
  // allocation input with the full remaining. Click again to reset to 0.
  // Disables auto-allocate so user intent is respected.
  const toggleAllocFill = (chargeId, remaining) => {
    setPaymentAutoAllocate(false);
    setPaymentAllocations(prev => {
      const current = parseFloat(prev[chargeId]) || 0;
      const filled = Number(remaining || 0).toFixed(2);
      const next = current > 0 ? '' : filled;
      return { ...prev, [chargeId]: next };
    });
  };

  const savePayment = async () => {
    const amount = parseFloat(paymentForm.amount);
    if (!amount || amount <= 0) {
      toast.error((t('amount') || 'Amount') + ' *');
      return;
    }
    const payrollMonth = paymentForm.payroll_month || '';
    if (!(payrollMonth && payrollMonth.length === 7 && payrollMonth[4] === '-')) {
      toast.error(t('payrollMonthRequired') || 'Payroll Month is required.');
      return;
    }
    try {
      if (paymentEditingId) {
        await axios.put(`${API}/cleaning/cleaners/${paymentCleanerId}/payments/${paymentEditingId}`, {
          amount,
          date: paymentForm.date,
          payroll_month: payrollMonth,
          notes: paymentForm.notes,
        }, axCfg);
      } else {
        await axios.post(`${API}/cleaning/cleaners/${paymentCleanerId}/payments`, {
          amount,
          date: paymentForm.date,
          payroll_month: payrollMonth,
          notes: paymentForm.notes,
        }, axCfg);
      }
      toast.success(t('saved'));
      setPaymentDialog(false);
      setPaymentEditingId(null);
      fetchCleaners();
      if (expandedCleaner === paymentCleanerId) {
        setCleanerPayments((await axios.get(`${API}/cleaning/cleaners/${paymentCleanerId}/payments`, axCfg)).data);
      }
      refreshCleanerDetail(paymentCleanerId);
      setCleanerInvoicesRefreshKey((k) => k + 1);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    }
  };
  const deleteClnrPayment = async (cleanerId, paymentId) => {
    try { await axios.delete(`${API}/cleaning/cleaners/${cleanerId}/payments/${paymentId}`, axCfg); toast.success(t('deleted')); fetchCleaners();
      if (expandedCleaner === cleanerId) setCleanerPayments((await axios.get(`${API}/cleaning/cleaners/${cleanerId}/payments`, axCfg)).data);
      refreshCleanerDetail(cleanerId);
      setCleanerInvoicesRefreshKey((k) => k + 1);
    } catch { toast.error('Error'); }
  };

  // === STATEMENT ===
  const openStatement = async (c) => {
    setStatementCleaner(c);
    setExpandedDates({});
    setStmtViewMode('all');
    // Inherit the current Job-History date filter into the Cleaner Statement.
    const df = jobDateFrom || '';
    const dt = jobDateTo || '';
    setStmtClnrFrom(df);
    setStmtClnrTo(dt);
    setStmtClnrDateMode(df || dt ? jobDateMode : 'all');
    const cid = jobClientFilter !== 'all' ? jobClientFilter : '';
    setStmtClnrClientFilter(jobClientFilter);
    // Build query with same filters as Job History
    const p = new URLSearchParams();
    if (df) p.append('date_from', df);
    if (dt) p.append('date_to', dt);
    if (cid) p.append('client_id', cid);
    try { setStatementData((await axios.get(`${API}/cleaning/cleaners/${c.cleaner_id}/statement?${p}`, axCfg)).data); } catch {}
  };
  const getPdfUrl = (c) => {
    if (!c?.cleaner_id) return '#';
    const params = { show_buildings: showBuildingsInPdf ? 'true' : 'false' };
    if (stmtClnrFrom) params.date_from = stmtClnrFrom;
    if (stmtClnrTo) params.date_to = stmtClnrTo;
    if (stmtClnrClientFilter && stmtClnrClientFilter !== 'all') params.client_id = stmtClnrClientFilter;
    return buildPdfUrl(`/cleaning/cleaners/${c.cleaner_id}/statement/pdf`, params);
  };

  // === CLIENT CRUD ===
  const openClientDialog = (c = null) => {
    setEditingClient(c);
    setClientForm(c ? { name: c.name || '', phone: c.phone || '', client_type: c.client_type || 'office', building_id: c.building_id || '', address: c.address || '', notes: c.notes || '' } : { name: '', phone: '', client_type: 'office', building_id: '', address: '', notes: '' });
    setClientDialog(true);
  };
  const saveClient = async () => {
    try { const p = { ...clientForm }; if (p.client_type !== 'building') p.building_id = '';
      if (editingClient) await axios.put(`${API}/cleaning/clients/${editingClient.client_id}`, p, axCfg); else await axios.post(`${API}/cleaning/clients`, p, axCfg);
      toast.success(t('saved')); setClientDialog(false); fetchClients();
    } catch (e) {
      // Phase 547 — Backend sends structured 409 errors as
      // detail = {code, message, existing_client_id}. Render only the
      // `message` string; never pass the raw object to toast.error so
      // React doesn't crash on "Objects are not valid as a React child".
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === 'string'
        ? detail
        : (detail?.message || e?.response?.data?.message || t('failedToCreateClient') || 'Failed to save client');
      toast.error(msg);
    }
  };
  // Phase 263 — Single-delete now respects the linked-data 409 response.
  const deleteClient = async () => {
    try {
      await axios.delete(`${API}/cleaning/clients/${deletingClient.client_id}`, axCfg);
      toast.success(t('deleted'));
      setDeletingClient(null);
      fetchClients();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && typeof detail === 'object' && detail?.linked_jobs) {
        toast.error(`${t('cannotDeleteLinked') || 'Cannot delete'} — ${detail.linked_jobs} ${t('jobs')}.`);
      } else {
        toast.error(typeof detail === 'string' ? detail : (detail?.message || 'Error'));
      }
    }
  };

  // === CLIENT PAYMENTS ===
  const openClientPayDialog = (c) => {
    setClientPayTarget(c);
    const now = new Date();
    setClientPayForm({ amount: '', date: now.toISOString().split('T')[0], period_month: String(now.getMonth() + 1), period_year: String(now.getFullYear()), notes: '' });
    setClientPayDialog(true);
  };
  const saveClientPayment = async () => {
    try { await axios.post(`${API}/cleaning/clients/${clientPayTarget.client_id}/payments`, clientPayForm, axCfg); toast.success(t('saved')); setClientPayDialog(false); fetchClients(); fetchJobs();
      if (expandedClient === clientPayTarget.client_id) { setClientHistory((await axios.get(`${API}/cleaning/clients/${clientPayTarget.client_id}/history`, axCfg)).data); }
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };
  const deleteClientPayment = async (clientId, paymentId) => {
    try { await axios.delete(`${API}/cleaning/clients/${clientId}/payments/${paymentId}`, axCfg); toast.success(t('deleted')); fetchClients();
      if (expandedClient === clientId) setClientHistory((await axios.get(`${API}/cleaning/clients/${clientId}/history`, axCfg)).data);
    } catch { toast.error('Error'); }
  };
  const toggleClientExpand = async (c) => {
    if (expandedClient === c.client_id) { setExpandedClient(null); return; }
    selectClient(c);
  };

  // Phase 471 — Select a client (used by both split-view rows and the
  // accordion expand toggle). Restores the last-opened sub-tab for the
  // client (defaults to "jobs") and lazily loads detail + sales data with
  // a skeleton state to avoid the empty-table flash.
  const selectClient = async (c) => {
    setExpandedClient(c.client_id);
    setClientViewMode(clientTabMemory[c.client_id] || 'jobs');
    setIsClientDetailLoading(true);
    try {
      const [histRes] = await Promise.all([
        axios.get(`${API}/cleaning/clients/${c.client_id}/history`, axCfg).catch(() => ({ data: null })),
      ]);
      if (histRes.data) setClientHistory(histRes.data);
    } finally {
      setIsClientDetailLoading(false);
    }
  };

  // Phase 471 — Wrap tab switching so we persist the user's last sub-tab
  // per client. Use this everywhere instead of calling setClientViewMode
  // directly when a client is actively selected.
  //
  // Phase 617 — Refetch the client's invoices + statement from the
  // canonical Sales source whenever the user lands on a money-screen
  // tab. The statement endpoint is already live-derived (no
  // description snapshots, no caching), but the React `clientStatements`
  // map IS a client-side cache, and that's how stale descriptions
  // appeared after the user edited an invoice in Finance and came back
  // here. Re-pulling on tab focus closes that drift window with zero
  // architectural cost.
  const setClientTab = (clientId, tabKey) => {
    setClientViewMode(tabKey);
    if (clientId) {
      setClientTabMemory(prev => ({ ...prev, [clientId]: tabKey }));
      if (['invoices', 'payments', 'statement'].includes(tabKey)) {
        fetchClientSalesData(clientId);
      }
    }
  };

  // Phase 268 — Pull invoices + statement from the Sales module so the
  // Cleaning Management → Clients view becomes invoice-driven (no longer
  // a summary-based Overall/Month roll-up).
  //
  // Phase 606 → 615 — User finalised the rule: CM mirrors ALL Finance
  // invoices for this client_id, no invoice_type filter. The UI groups
  // them visually (IC-* vs SI-*) so nothing is hidden but both
  // populations are immediately distinguishable.
  const fetchClientSalesData = async (clientId) => {
    try {
      // Phase 618 — Building-type clients store invoices keyed by
      // building_id (not client_id). Pass BOTH so the Invoices tab and
      // the statement match the canonical Finance dataset for every
      // client_type. The endpoint already supports `include_linked_buildings`.
      const c = clients.find((x) => x.client_id === clientId);
      const bid = c?.building_id || '';
      const invUrl = bid
        ? `${API}/sales/invoices?client_id=${clientId}&building_id=${bid}`
        : `${API}/sales/invoices?client_id=${clientId}`;
      const [invRes, stmtRes] = await Promise.all([
        axios.get(invUrl, axCfg),
        axios.get(`${API}/sales/customers/${clientId}/statement`, axCfg),
      ]);
      const all = (invRes.data || []).filter((i) => !i?.is_deleted);
      setClientInvoices(prev => ({ ...prev, [clientId]: all }));
      setClientStatements(prev => ({ ...prev, [clientId]: stmtRes.data || null }));
    } catch (e) {
      // Sales module is optional — fail silently and keep the rest of
      // the row functional.
    }
  };

  const refreshClientSalesData = (clientId) => fetchClientSalesData(clientId);

  // Phase 605 — Inline invoice CRUD removed. Cleaning Management now
  // operates as a strict READ-ONLY mirror of the canonical Finance/Sales
  // `sales_invoices` source. Users create, edit and delete invoices
  // exclusively from the /sales module. This eliminates the entire
  // class of "phantom invoice" and "edit not refreshing" bugs by removing
  // the only secondary write path that ever existed on this screen.
  //
  // The list rendering below (under "INVOICES — Phase 479") fetches
  // straight from /api/sales/invoices?client_id=X — same endpoint Sales
  // uses — and re-fetches via `refreshClientSalesData` whenever the user
  // returns to this view.

  // Deep-link a row to the canonical Sales/Invoices screen so the user
  // can edit / clone / delete it from the single source of truth.
  // Phase 615 — Route to the matching tab based on the invoice type
  // since CM now shows BOTH IC-* and SI-* invoices.
  const openInvoiceInFinance = (invoice) => {
    const tab = invoice?.invoice_type === 'internal_cash_tracking'
      ? 'internal_invoices'
      : 'invoices';
    const search = encodeURIComponent(invoice?.invoice_no || '');
    navigate(`/sales?tab=${tab}&search=${search}`);
  };

  // === JOB CRUD (Multi-cleaner) ===
  const openJobDialog = async (j = null) => {
    setEditingJob(j);
    if (j) {
      // Phase 712.2 — The list endpoint flattens multi-cleaner jobs
      // into per-cleaner rows for the table (`_flat_id` suffix), which
      // means `j.cleaners` only contains the single cleaner of the
      // clicked row. To preload EVERY assignment we refetch the
      // unflattened job by `job_id`. We optimistically render with the
      // row data first so the modal opens instantly, then upgrade the
      // cleaner list once the fetch resolves.
      const seed = (j.cleaners || []).map(cl => ({
        cleaner_id: cl.cleaner_id || '',
        // Important: don't fall back to '' when value is 0 — preserve
        // legitimate zero values entered by the user.
        hours_worked: (cl.hours_worked === 0 || cl.hours_worked) ? String(cl.hours_worked) : '',
        cleaner_earned: (cl.cleaner_earned === 0 || cl.cleaner_earned) ? String(cl.cleaner_earned) : '',
      }));
      setJobCleanerRows(seed.length ? seed : [{ cleaner_id: '', hours_worked: '', cleaner_earned: '' }]);
      setJobForm({ date: j.date || '', client_id: j.client_id || '', notes: j.notes || '' });
      setJobDialog(true);
      try {
        const r = await axios.get(`${API}/cleaning/jobs/${j.job_id}`, axCfg);
        const full = r.data;
        if (full && Array.isArray(full.cleaners)) {
          const rows = full.cleaners.map(cl => ({
            cleaner_id: cl.cleaner_id || '',
            hours_worked: (cl.hours_worked === 0 || cl.hours_worked) ? String(cl.hours_worked) : '',
            cleaner_earned: (cl.cleaner_earned === 0 || cl.cleaner_earned) ? String(cl.cleaner_earned) : '',
          }));
          setJobCleanerRows(rows.length ? rows : [{ cleaner_id: '', hours_worked: '', cleaner_earned: '' }]);
          setJobForm({ date: full.date || '', client_id: full.client_id || '', notes: full.notes || '' });
          // Replace `editingJob` so the PUT call uses the canonical
          // job_id (not `_flat_id`) and our save handler keeps working.
          setEditingJob(full);
        }
      } catch (e) {
        console.error('[CleaningManagement] failed to fetch full job for edit', e);
      }
    } else {
      setJobCleanerRows([{ cleaner_id: '', hours_worked: '', cleaner_earned: '' }]);
      setJobForm({ date: new Date().toISOString().split('T')[0], client_id: '', notes: '' });
      setJobDialog(true);
    }
  };
  const addCleanerRow = () => { if (jobCleanerRows.length < 10) setJobCleanerRows([...jobCleanerRows, { cleaner_id: '', hours_worked: '', cleaner_earned: '' }]); };
  const removeCleanerRow = (idx) => { if (jobCleanerRows.length > 1) setJobCleanerRows(jobCleanerRows.filter((_, i) => i !== idx)); };
  const updateCleanerRow = (idx, field, value) => {
    const rows = [...jobCleanerRows];
    rows[idx] = { ...rows[idx], [field]: value };
    // Auto-calc earned if hourly
    // Phase 265 — Parse hours in either decimal (`4.75`) or HH:MM (`4:45`)
    // formats. The UI never force-rounds to `.toFixed` — we keep the raw
    // multiplication result and only format the displayed currency
    // with `.toFixed(2)` in the `value` binding below.
    if (field === 'hours_worked' || field === 'cleaner_id') {
      const c = cleaners.find(cl => cl.cleaner_id === rows[idx].cleaner_id);
      if (c && c.payment_type === 'hourly') {
        const hrs = parseTimeToHours(rows[idx].hours_worked);
        const raw = hrs * (c.hourly_rate || 0);
        // Match the previous formatting (2dp for EUR). The value sent to
        // the backend is reparsed from this string at save time.
        rows[idx].cleaner_earned = raw > 0 ? raw.toFixed(2) : '';
      }
    }
    setJobCleanerRows(rows);
  };
  const jobTotalCleanerCost = jobCleanerRows.reduce((s, r) => s + (parseFloat(r.cleaner_earned) || 0), 0);

  const saveJob = async () => {
    const validRows = jobCleanerRows.filter(r => r.cleaner_id);
    if (!validRows.length) { toast.error(t('selectCleaner')); return; }
    // Phase 265 — Normalise hours to decimal before sending. Accept both
    // `4.75` and `4:45`. Never pass the raw string to the backend.
    const normalizedRows = validRows.map(r => ({
      ...r,
      hours_worked: parseTimeToHours(r.hours_worked),
      cleaner_earned: parseFloat(r.cleaner_earned) || 0,
    }));
    const payload = { ...jobForm, cleaners: normalizedRows };
    try {
      if (editingJob) await axios.put(`${API}/cleaning/jobs/${editingJob.job_id}`, payload, axCfg);
      else await axios.post(`${API}/cleaning/jobs`, payload, axCfg);
      toast.success(t('saved')); setJobDialog(false); fetchJobs(); fetchCleaners(); fetchClients();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };
  const deleteJob = async () => { try { await axios.delete(`${API}/cleaning/jobs/${deletingJob.job_id}`, axCfg); toast.success(t('deleted')); setDeletingJob(null); fetchJobs(); fetchCleaners(); fetchClients(); } catch { toast.error('Error'); } };

  // === CATEGORIES ===
  const openCatDialog = (c = null) => {
    setEditingCat(c);
    setCatForm(c ? { name: c.name || '', name_el: c.name_el || '' } : { name: '', name_el: '' });
    setCatDialog(true);
  };
  const saveCat = async () => {
    try {
      if (editingCat) await axios.put(`${API}/cleaning/categories/${editingCat.category_id}`, catForm, axCfg);
      else await axios.post(`${API}/cleaning/categories`, catForm, axCfg);
      toast.success(t('saved')); setCatDialog(false); setEditingCat(null); fetchCategories();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };
  const deleteCat = async () => { try { await axios.delete(`${API}/cleaning/categories/${deletingCat.category_id}`, axCfg); toast.success(t('deleted')); setDeletingCat(null); fetchCategories(); } catch { toast.error('Error'); } };

  // === PDF Export ===
  const exportJobsPdf = () => {
    const params = {};
    if (jobCleanerFilter !== 'all') params.cleaner_id = jobCleanerFilter;
    if (jobClientFilter !== 'all') params.client_id = jobClientFilter;
    if (jobDateFrom) params.date_from = jobDateFrom;
    if (jobDateTo) params.date_to = jobDateTo;
    params.sort_order = sortOrder;
    downloadPdfUtil('/cleaning/jobs/export/pdf', params);
  };

  // === Financial Statement ===
  const fetchFinancialStatement = async () => {
    setStmtLoading(true);
    try {
      const p = new URLSearchParams();
      if (stmtFrom) p.append('start_date', stmtFrom);
      if (stmtTo) p.append('end_date', stmtTo);
      setFinancialData((await axios.get(`${API}/cleaning/financial-statement?${p}`, axCfg)).data);
    } catch { toast.error(t('errorOccurred')); }
    finally { setStmtLoading(false); }
  };
  const downloadFinancialPdf = () => {
    const params = {};
    if (stmtFrom) params.start_date = stmtFrom;
    if (stmtTo) params.end_date = stmtTo;
    params.sort_order = sortOrder;
    downloadPdfUtil('/cleaning/financial-statement/pdf', params);
  };
  const reloadCleanerStatement = async () => {
    if (!statementCleaner) return;
    const p = new URLSearchParams();
    if (stmtClnrFrom) p.append('date_from', stmtClnrFrom);
    if (stmtClnrTo) p.append('date_to', stmtClnrTo);
    if (stmtClnrClientFilter && stmtClnrClientFilter !== 'all') p.append('client_id', stmtClnrClientFilter);
    try { setStatementData((await axios.get(`${API}/cleaning/cleaners/${statementCleaner.cleaner_id}/statement?${p}`, axCfg)).data); } catch {}
  };

  // Phase 366 — Helper: stable date sort. Empty/null dates always sort last.
  const sortByDate = useCallback((arr, key = 'date') => {
    if (!Array.isArray(arr)) return [];
    return [...arr].sort((a, b) => {
      const da = a?.[key] || '', dbb = b?.[key] || '';
      // Empties go to the end regardless of direction
      if (!da && !dbb) return 0;
      if (!da) return 1;
      if (!dbb) return -1;
      const cmp = da.localeCompare(dbb);
      return sortOrder === 'asc' ? cmp : -cmp;
    });
  }, [sortOrder]);

  // Sorted views of the Cleaner Statement payload
  const sortedBuildingsCleaning = useMemo(() => sortByDate(statementData?.buildings_cleaning || []), [statementData, sortByDate]);
  const sortedJobHistoryDetails = useMemo(() => sortByDate(statementData?.job_history_details || []), [statementData, sortByDate]);
  const sortedClnrStmtPayments = useMemo(() => sortByDate(statementData?.payments || []), [statementData, sortByDate]);

  // Sorted views of the Financial Statement payload
  const sortedIncomePayments = useMemo(() => sortByDate(financialData?.income?.payments || []), [financialData, sortByDate]);
  const sortedExpenseCleaners = useMemo(() => {
    if (!financialData?.expenses?.cleaners) return [];
    return financialData.expenses.cleaners.map(c => ({ ...c, entries: sortByDate(c.entries || []) }));
  }, [financialData, sortByDate]);

  // Phase 366 — Inline sort-order toggle button. Tap to flip asc/desc.
  const SortToggle = ({ value = sortOrder, onChange = setSortOrder, testId = 'sort-toggle', size = 'sm' }) => {
    const h = size === 'md' ? 'h-9' : 'h-8';
    return (
      <button
        type="button"
        onClick={() => onChange(value === 'asc' ? 'desc' : 'asc')}
        className={`inline-flex items-center gap-1.5 ${h} px-2.5 border border-zinc-300 bg-white text-xs text-zinc-700 hover:bg-zinc-50 rounded-none`}
        data-testid={testId}
        title={value === 'asc' ? (t('oldestFirst') || 'Oldest first') : (t('newestFirst') || 'Newest first')}
      >
        {value === 'asc' ? <SortAscending size={14} /> : <SortDescending size={14} />}
        <span className="font-medium">{value === 'asc' ? (t('oldestFirst') || 'Oldest first') : (t('newestFirst') || 'Newest first')}</span>
      </button>
    );
  };

  // Phase 475 — Combined view rebuilt as a FLAT timeline. The previous
  // implementation grouped by date and rendered generic "Building Cleaning
  // ·N" / "Job History ·N" pills, which left the user without an
  // immediate answer to "who was the work for?". Each entry now becomes
  // a single row whose primary identifier is the actual client or
  // building name. The job/building type is preserved as a secondary
  // category badge.
  const combinedStmtRows = useMemo(() => {
    if (!statementData) return [];
    const rows = [];
    (statementData.buildings_cleaning || []).forEach(day => {
      (day.buildings || []).forEach(b => {
        const cleaningType = (b.cleaning_type || 'general_cleaning')
          .replace(/_/g, ' ')
          .replace(/\b\w/g, (m) => m.toUpperCase());
        rows.push({
          date: day.date,
          kind: 'building',
          name: b.building_name || b.building_id || '—',
          description: cleaningType,
          categoryLabel: t('building') || 'Building',
          categoryTone: 'amber',
          hours: 0,
          amount: Number(b.amount) || 0,
          raw: { building_id: b.building_id, cleaning_type: b.cleaning_type, day_id: day.day_id || day.date },
        });
      });
    });
    (statementData.job_history_details || []).forEach(j => {
      rows.push({
        date: j.date,
        kind: 'job',
        name: j.client_name || j.client_id || '—',
        description: j.notes || j.client_type || '',
        categoryLabel: (j.client_type
          ? (j.client_type.replace(/_/g, ' ').replace(/\b\w/g, m => m.toUpperCase()))
          : (t('jobs') || 'Job')),
        categoryTone: 'emerald',
        hours: Number(j.hours_worked) || 0,
        amount: Number(j.amount) || 0,
        raw: { client_id: j.client_id, job_id: j.job_id || j._flat_id },
      });
    });
    return rows.sort((a, b) => {
      const cmp = (a.date || '').localeCompare(b.date || '');
      if (cmp !== 0) return sortOrder === 'asc' ? cmp : -cmp;
      // Secondary sort by name so days with multiple entries are
      // alphabetically scannable.
      return (a.name || '').localeCompare(b.name || '');
    });
  }, [statementData, sortOrder, t]);

  // Phase 475 — Client/Building filter for the combined view.
  const [stmtNameFilter, setStmtNameFilter] = useState('all');
  const stmtNameOptions = useMemo(() => {
    const set = new Set();
    combinedStmtRows.forEach(r => { if (r.name) set.add(r.name); });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [combinedStmtRows]);
  const filteredCombinedStmtRows = useMemo(() => {
    if (stmtNameFilter === 'all') return combinedStmtRows;
    return combinedStmtRows.filter(r => r.name === stmtNameFilter);
  }, [combinedStmtRows, stmtNameFilter]);

  // === Filtered data ===
  const filteredCleaners = useMemo(() => {
    let result = cleaners;
    if (cleanerSearch) {
      const q = cleanerSearch.toLowerCase();
      result = result.filter(c => c.name?.toLowerCase().includes(q) || c.phone?.includes(q));
    }
    // Phase 735.43 — Alphabetical (A-Z) sort ALWAYS. Never sort by
    // balance / amount / activity — the dropdown UX depends on a
    // predictable order the user can navigate with keyboard.
    return [...result].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [cleaners, cleanerSearch]);

  // Phase 735.43 — Per-cleaner payment status used by the searchable
  // cleaner dropdown badges. Derived from the lifetime totals returned
  // by the listing endpoint (`total_earned` / `total_paid`) so it's
  // available immediately without extra fetches:
  //   PAID    → total_paid ≥ total_earned  (or nothing earned yet)
  //   UNPAID  → total_paid ≈ 0 with earnings outstanding
  //   PARTIAL → some paid, some outstanding
  const getCleanerPaymentStatus = useCallback((c) => {
    const earned = Number(c?.total_earned || 0);
    const paid = Number(c?.total_paid || 0);
    if (earned <= 0.005) return 'paid';
    if (paid + 0.005 >= earned) return 'paid';
    if (paid <= 0.005) return 'unpaid';
    return 'partial';
  }, []);
  const filteredClients = useMemo(() => {
    let list = clientTypeFilter === 'all' ? clients : clients.filter(c => c.client_type === clientTypeFilter);
    if (clientSearch) {
      const q = clientSearch.toLowerCase();
      list = list.filter(c => c.name?.toLowerCase().includes(q) || c.phone?.includes(q) || c.address?.toLowerCase().includes(q));
    }
    // Phase 625 — Default sort: clients with positive outstanding
    // balance first (largest debt at top), then zero-balance clients,
    // then credit-balance (negative) clients last. Balance source is
    // the canonical `customer_balance` field (Phase 622) so the order
    // matches the sidebar pill, Outstanding KPI and Statement final
    // row. Within the same balance bucket we keep alphabetical order
    // for stability.
    list = [...list].sort((a, b) => {
      const ba = Number(a.customer_balance ?? 0);
      const bb = Number(b.customer_balance ?? 0);
      // Bucket: 0 = owes money, 1 = paid in full, 2 = credit balance.
      const bucket = (x) => (x > 0.005 ? 0 : x < -0.005 ? 2 : 1);
      const ka = bucket(ba), kb = bucket(bb);
      if (ka !== kb) return ka - kb;
      // Within the "owes" bucket sort by amount DESC. Within the
      // "credit" bucket sort by amount ASC (largest credit first =
      // most negative). Zero bucket is alphabetical.
      if (ka === 0) {
        if (ba !== bb) return bb - ba;
      } else if (ka === 2) {
        if (ba !== bb) return ba - bb;
      }
      return (a.name || '').localeCompare(b.name || '');
    });
    return list;
  }, [clients, clientTypeFilter, clientSearch]);
  const filteredJobs = useMemo(() => {
    let list = jobs;
    // Phase 365 — Manual-only is now permanent (auto rows are hidden always).
    list = list.filter(j => j.source !== 'cleaning_history_auto');
    if (jobCleanerFilter !== 'all') list = list.filter(j => j.cleaners?.some(cl => cl.cleaner_id === jobCleanerFilter));
    if (jobClientFilter !== 'all') list = list.filter(j => j.client_id === jobClientFilter);
    if (jobDateFrom) list = list.filter(j => j.date && j.date >= jobDateFrom);
    if (jobDateTo) list = list.filter(j => j.date && j.date <= jobDateTo);
    if (jobSearch) { const q = jobSearch.toLowerCase(); list = list.filter(j => j.cleaner_names?.toLowerCase().includes(q) || j.client_name?.toLowerCase().includes(q) || j.notes?.toLowerCase().includes(q)); }
    // Phase 366 — Sort by date according to user-selected order.
    list = [...list].sort((a, b) => {
      const da = a.date || '', db = b.date || '';
      const cmp = da.localeCompare(db);
      return sortOrder === 'asc' ? cmp : -cmp;
    });
    return list;
  }, [jobs, jobCleanerFilter, jobClientFilter, jobDateFrom, jobDateTo, jobSearch, sortOrder]);

  const jobTotals = useMemo(() => {
    const totalCost = filteredJobs.reduce((s, j) => s + (j.total_cleaner_cost || 0), 0);
    const totalHours = filteredJobs.reduce((s, j) => s + (j.total_hours || 0), 0);
    return { totalJobs: filteredJobs.length, totalHours, totalCleanerCost: totalCost };
  }, [filteredJobs]);

  const getClientTypeLabel = (ct) => {
    if (ct === 'building') return t('buildings'); if (ct === 'office') return t('office'); if (ct === 'house') return t('house'); if (ct === 'general_cleaning') return t('generalCleaning');
    const cat = categories.find(c => c.name.toLowerCase().replace(/\s+/g, '_') === ct);
    return cat ? (language === 'el' ? (cat.name_el || cat.name) : cat.name) : ct;
  };
  const getClientIcon = (ct) => { if (ct === 'building') return Buildings; if (ct === 'office') return OfficeChair; if (ct === 'house') return House; return Broom; };
  const getClientColor = (ct) => { if (ct === 'building') return 'bg-blue-50 text-blue-700 border-blue-200'; if (ct === 'office') return 'bg-violet-50 text-violet-700 border-violet-200'; if (ct === 'house') return 'bg-emerald-50 text-emerald-700 border-emerald-200'; return 'bg-amber-50 text-amber-700 border-amber-200'; };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto" data-testid="cleaning-management-page">
        <h1 className="text-2xl font-bold text-zinc-900 tracking-tight mb-4">{t('cleaningManagement')}</h1>

        {/* Phase 103 — Global totals across ALL clients and ALL categories.
            Phase 614 — Same operational metric used by per-client cards and
            sidebar so all three reconcile to the cent. */}
        {(() => {
          const g = clients.reduce((acc, c) => {
            const inv = Number(c.total_invoiced || 0);
            const cost = Number(c.total_cost || 0);
            const pOp = (typeof c.profit_operational === 'number') ? c.profit_operational : (inv - cost);
            return {
              invoiced: acc.invoiced + inv,
              cost: acc.cost + cost,
              profit: acc.profit + pOp,
            };
          }, { invoiced: 0, cost: 0, profit: 0 });
          return (
            <div className="flex items-stretch w-full border border-zinc-200 divide-x divide-zinc-200 mb-6" data-testid="cleaning-global-totals">
              <div className="flex-1 min-w-[140px] px-4 py-2">
                <div className="text-[9px] font-semibold uppercase tracking-wider text-zinc-400">{t('overallCleaning')}</div>
                <div className="text-xs text-zinc-500 font-mono">{clients.length} {clients.length === 1 ? t('client') : t('clients').toLowerCase()} · {t('allCategories')}</div>
              </div>
              <div className="flex-1 min-w-[140px] basis-0 px-4 py-2" data-testid="cleaning-global-paid">
                <div className="text-[9px] font-semibold uppercase tracking-wider text-zinc-500">{t('totalInvoiced') || 'Invoiced'}</div>
                <div className="text-sm font-mono font-bold tabular-nums text-zinc-800">&euro;{g.invoiced.toFixed(2)}</div>
              </div>
              <div className="flex-1 min-w-[140px] basis-0 px-4 py-2" data-testid="cleaning-global-cost">
                <div className="text-[9px] font-semibold uppercase tracking-wider text-amber-600">{t('totalCleanerCost')}</div>
                <div className="text-sm font-mono font-bold tabular-nums text-amber-800">&euro;{g.cost.toFixed(2)}</div>
              </div>
              <div className={`flex-1 min-w-[140px] basis-0 px-4 py-2 ${g.profit >= 0 ? 'bg-teal-50' : 'bg-red-50'}`} data-testid="cleaning-global-profit">
                <div className={`text-[9px] font-semibold uppercase tracking-wider ${g.profit >= 0 ? 'text-teal-600' : 'text-red-600'}`}>{t('totalProfit')} <span className="text-[8px] font-normal normal-case text-zinc-400">({t('invoicedMinusCost') || 'invoiced − cost'})</span></div>
                <div className={`text-sm font-mono font-extrabold tabular-nums ${g.profit >= 0 ? 'text-teal-900' : 'text-red-900'}`}>&euro;{g.profit.toFixed(2)}</div>
              </div>
            </div>
          );
        })()}

        <div className="flex gap-0 border-b border-zinc-200 mb-6" data-testid="cleaning-tabs">
          {TABS.map(tab => (
            <button key={tab} onClick={() => setActiveTab(tab)} className={`px-5 py-2.5 text-sm font-medium border-b-2 transition-colors ${activeTab === tab ? 'border-zinc-900 text-zinc-900' : 'border-transparent text-zinc-500 hover:text-zinc-700'}`} data-testid={`cleaning-tab-${tab}`}>
              {tab === 'cleaners' && <UserCircle size={16} className="inline mr-1.5 -mt-0.5" />}
              {tab === 'clients' && <Users size={16} className="inline mr-1.5 -mt-0.5" />}
              {tab === 'jobHistory' && <ClockCounterClockwise size={16} className="inline mr-1.5 -mt-0.5" />}
              {tab === 'cleaningHistory' && <Broom size={16} className="inline mr-1.5 -mt-0.5" />}
              {tab === 'statement' && <CurrencyEur size={16} className="inline mr-1.5 -mt-0.5" />}
              {tab === 'categories' && <Tag size={16} className="inline mr-1.5 -mt-0.5" />}
              {tab === 'cleaningSchedule' && <CalendarBlank size={16} className="inline mr-1.5 -mt-0.5" />}
              {tab === 'cleaningSchedule' ? 'Cleaning Schedule' : t(tab)}
            </button>
          ))}
        </div>

        {/* ============ CLEANERS TAB ============ */}
        {activeTab === 'cleaners' && (
          <div data-testid="cleaners-tab">
            <div className="flex justify-between items-center mb-4 gap-3 flex-wrap">
              <div className="flex items-center gap-3 flex-wrap">
                <p className="text-sm text-zinc-500">{filteredCleaners.length} {t('cleaners').toLowerCase()}</p>
                <div className="relative"><MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400" /><Input value={cleanerSearch} onChange={e => setCleanerSearch(e.target.value)} placeholder={t('search')} className="rounded-none pl-8 h-8 w-48 text-xs" data-testid="cleaner-search" /></div>
                {/* Phase 602 — Status tabs (Active / Inactive / All).
                    Default = Active so the operational list stays clean.
                    Switching to Inactive surfaces archived cleaners for
                    review / reactivation; All shows everyone. */}
                <div className="flex items-center gap-0 border border-zinc-200 bg-white" data-testid="cleaner-status-tabs">
                  {[
                    { k: 'active',   label: t('active') || 'Active' },
                    { k: 'inactive', label: t('inactive') || 'Inactive' },
                    { k: 'all',      label: t('all') || 'All' },
                  ].map(opt => (
                    <button
                      key={opt.k}
                      onClick={() => setCleanerStatusFilter(opt.k)}
                      className={`px-2.5 py-1 text-[11px] font-medium uppercase tracking-wider transition ${cleanerStatusFilter === opt.k ? 'bg-zinc-900 text-white' : 'text-zinc-500 hover:bg-zinc-100'}`}
                      data-testid={`cleaner-status-tab-${opt.k}`}
                    >{opt.label}</button>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {/* Phase 735.43 — LIST mode removed entirely. The
                    cleaner selector below is a searchable dropdown
                    (Command+Popover), so a separate "list vs split"
                    toggle no longer makes sense. */}
                <Button onClick={() => openCleanerDialog()} size="sm" className="rounded-none" data-testid="add-cleaner-btn"><Plus size={16} className="mr-1" /> {t('addCleaner')}</Button>
              </div>
            </div>
            {/* Phase 263 — Bulk action bar (visible when ≥1 cleaner selected). */}
            {selectedCleanerIds.size > 0 && (
              <div className="flex items-center justify-between mb-3 px-3 py-2 bg-zinc-900 text-white" data-testid="cleaners-bulk-bar">
                <div className="text-xs">
                  <strong>{selectedCleanerIds.size}</strong> {t('selected') || 'selected'}
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => clearSelection('cleaner')} className="text-xs text-zinc-300 hover:text-white px-2 py-1" data-testid="cleaners-clear-selection">{t('clear') || 'Clear'}</button>
                  <Button size="sm" variant="destructive" className="rounded-none h-7 text-xs" onClick={() => setBulkDeleteConfirm({ kind: 'cleaner', ids: [...selectedCleanerIds] })} data-testid="cleaners-bulk-delete-btn">
                    <Trash size={14} className="mr-1" /> {t('deleteSelected') || 'Delete Selected'}
                  </Button>
                </div>
              </div>
            )}
            {/* Phase 735.53 — Removed the "Select all" checkbox from the
                Cleaners page. The new UNPAID-first selector below is the
                primary interaction; bulk selection was rarely used here
                and only added visual noise above the important list. */}
            {/* Phase 735.43 — Accordion (legacy List) view removed entirely.
                The searchable dropdown below replaces both the list and
                the left sidebar of the split view. */}

            {/* Split master/detail — Phase 735.43 uses a full-width right
                panel and a searchable cleaner dropdown at the top instead
                of a vertical sidebar. */}
            {(() => {
              const selectedC = filteredCleaners.find(cc => cc.cleaner_id === expandedCleaner) || null;
              const stmt = selectedC ? cleanerStatements[selectedC.cleaner_id] : null;
              const jobsList = stmt?.job_history_details || [];
              const buildingsList = stmt?.buildings_cleaning || [];
              const paymentsList = stmt?.payments || [];
              const totalJobsCount = jobsList.length;
              const totalBuildingsCount = (stmt?.buildings_entry_count ?? buildingsList.reduce((s, d) => s + (d.entry_count || 0), 0));
              // Phase 516 / 610 — When a date filter is active, ALL summary
              // cards must reconcile with the visible rows. Otherwise the
              // user sees "filtered statement" rows but "lifetime" totals,
              // which is the exact drift Phase 609 forbids. We recompute
              // totalEarned / totalPaid from the same filtered lists the
              // tab tables render below.
              const inlineApplied = selectedC ? getInlineFilter(selectedC.cleaner_id).applied : { from: '', to: '' };
              const filterActive = !!(inlineApplied.from || inlineApplied.to);
              const r2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;
              let totalEarned, totalPaid, remaining;
              if (filterActive) {
                const earnedJobs = jobsList.reduce((s, j) => s + Number(j.cleaner_earned || 0), 0);
                const earnedBuildings = buildingsList.reduce((s, b) => s + Number(b.total_amount || b.cleaner_payment || 0), 0);
                const paidSum = paymentsList.reduce((s, p) => s + Number(p.amount || 0), 0);
                totalEarned = r2(earnedJobs + earnedBuildings);
                totalPaid = r2(paidSum);
                remaining = r2(totalEarned - totalPaid);
              } else {
                totalEarned = stmt?.total_earned ?? selectedC?.total_earned ?? 0;
                totalPaid = stmt?.total_paid ?? selectedC?.total_paid ?? 0;
                remaining = stmt?.remaining_balance ?? selectedC?.remaining_balance ?? 0;
              }
              const totalHours = filterActive
                ? jobsList.reduce((s, j) => s + Number(j.hours_worked || 0), 0)
                : (selectedC?.total_hours || 0);
              const allDates = [
                ...jobsList.map(j => j.date),
                ...buildingsList.map(b => b.date),
              ].filter(Boolean);
              const lastJobDate = allDates.length ? allDates.reduce((a, b) => (a > b ? a : b)) : null;
              const avgHourly = totalHours > 0 ? (totalEarned / totalHours) : 0;
              // Data-mismatch banner: if cleaners-list aggregate doesn't equal
              // the statement detail, something is hidden by a stale filter.
              // Phase 516 — Suppressed while a date filter is active (the
              // mismatch would always trigger by design).
              const expectedJobs = (selectedC?.total_jobs ?? 0) + (selectedC?.total_buildings_jobs ?? 0);
              const actualJobs = totalJobsCount + totalBuildingsCount;
              const mismatch = !filterActive && selectedC && expectedJobs !== actualJobs;
              const activeTab = selectedC ? (cleanerTabMemory[selectedC.cleaner_id] || cleanerViewMode || 'jobs') : 'jobs';
              return (
              <div className="space-y-3" data-testid="cleaners-split-view">
                {/* Phase 735.43 — Searchable Cleaner Dropdown replaces
                    the vertical sidebar. Alphabetical order (enforced by
                    `filteredCleaners`), keyboard nav + auto-focus search
                    via `Command`, quick payment-status filter chips
                    (All / Paid / Unpaid / Partial) and a Paid/Partial/
                    Unpaid badge on every row. */}
                <CleanerSelectorDropdown
                  cleaners={filteredCleaners}
                  selectedCleanerId={expandedCleaner}
                  onSelect={(c) => selectCleaner(c)}
                  getPaymentStatus={getCleanerPaymentStatus}
                  paymentStatusFilter={cleanerPaymentStatusFilter}
                  onPaymentStatusFilterChange={setCleanerPaymentStatusFilter}
                  t={t}
                />
                {/* RIGHT — detail panel (now full-width). */}
                <div className="bg-white border border-zinc-200 min-h-[400px]" data-testid="cleaners-split-right">
                  {!selectedC ? (
                    <div className="flex items-center justify-center min-h-[400px] text-center px-6 py-12" data-testid="cleaner-split-no-selection">
                      <div>
                        <UserCircle size={48} className="mx-auto text-zinc-300 mb-3" />
                        <p className="text-sm text-zinc-500">{t('selectACleaner') || 'Select a cleaner from the list'}</p>
                        <p className="text-xs text-zinc-400 mt-1">{t('selectACleanerHint') || 'Pick a cleaner from the dropdown above to view jobs, buildings, payments and statement.'}</p>
                      </div>
                    </div>
                  ) : isCleanerDetailLoading ? (
                    <div className="p-6 space-y-3" data-testid="cleaner-split-loading">
                      <div className="h-6 bg-zinc-100 animate-pulse w-1/3"></div>
                      <div className="grid grid-cols-6 gap-2">
                        {[0,1,2,3,4,5].map(i => <div key={i} className="h-16 bg-zinc-100 animate-pulse"></div>)}
                      </div>
                      <div className="h-8 bg-zinc-100 animate-pulse w-2/3"></div>
                      <div className="h-64 bg-zinc-100 animate-pulse"></div>
                    </div>
                  ) : (
                    <div>
                      {/* Sticky action bar */}
                      <div className="sticky top-0 z-10 flex items-center justify-between gap-3 px-5 py-3 bg-white border-b border-zinc-200" data-testid="cleaner-action-bar">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-10 h-10 rounded-full bg-zinc-100 flex items-center justify-center"><UserCircle size={22} className="text-zinc-400" /></div>
                          <div className="min-w-0">
                            <h3 className="font-bold text-zinc-900 truncate text-base">{selectedC.name}</h3>
                            <div className="flex items-center gap-2 text-xs text-zinc-500 mt-0.5">
                              {selectedC.phone && <span className="flex items-center gap-1"><Phone size={11} /> {selectedC.phone}</span>}
                              <span className={`px-1.5 py-0.5 font-medium border ${selectedC.payment_type === 'hourly' ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`}>
                                {selectedC.payment_type === 'hourly' ? `${t('perHour')} - €${selectedC.hourly_rate}` : `${t('perMonth')} - €${selectedC.monthly_rate}`}
                              </span>
                              {/* Phase 606 — Inactive badge in the header. */}
                              {(selectedC.status || 'active') === 'inactive' && (
                                <span className="px-1.5 py-0.5 font-bold uppercase tracking-wider border bg-red-50 text-red-700 border-red-200 inline-flex items-center gap-1" data-testid={`split-cleaner-inactive-badge-${selectedC.cleaner_id}`}>
                                  <Archive size={10} weight="bold" /> {t('inactive') || 'Inactive'}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          {/* Phase 606 — Block creation actions when the cleaner is inactive. */}
                          {(() => { const isInactive = (selectedC.status || 'active') === 'inactive'; return (<>
                          <Button size="sm" className="rounded-none h-8 text-xs" disabled={isInactive} title={isInactive ? (t('cleanerInactiveBlocksAdd') || 'Cleaner is inactive — reactivate to add jobs.') : undefined} onClick={() => { if (isInactive) return; setJobForm({ date: new Date().toISOString().split('T')[0], client_id: '', notes: '' }); setJobCleanerRows([{ cleaner_id: selectedC.cleaner_id, hours_worked: '', cleaner_earned: '' }]); setEditingJob(null); setJobDialog(true); }} data-testid={`split-cleaner-add-job-${selectedC.cleaner_id}`}>
                            <Plus size={13} className="mr-1" /> {t('addJob') || 'Add Job'}
                          </Button>
                          {/* Phase 510 — "Add Building Cleaning" shortcut.
                              Switches to the embedded Building Cleaning tab
                              and signals it to open its Add dialog with this
                              cleaner preselected, mirroring the exact flow
                              used in the standalone Building Cleaning page so
                              no data path diverges. */}
                          <Button size="sm" variant="outline" className="rounded-none h-8 text-xs" disabled={isInactive} title={isInactive ? (t('cleanerInactiveBlocksAdd') || 'Cleaner is inactive — reactivate to add jobs.') : undefined} onClick={() => {
                            if (isInactive) return;
                            setAddBuildingCleaningPreset({ cleaner_id: selectedC.cleaner_id, name: selectedC.name });
                            setAddBuildingCleaningSignal(s => s + 1);
                            setActiveTab('cleaningHistory');
                          }} data-testid={`split-cleaner-add-building-cleaning-${selectedC.cleaner_id}`}>
                            <Broom size={13} className="mr-1" /> {t('addBuildingCleaning') || 'Add Building Cleaning'}
                          </Button>
                          <Button size="sm" variant="outline" className="rounded-none h-8 text-xs" disabled={isInactive} title={isInactive ? (t('cleanerInactiveBlocksAdd') || 'Cleaner is inactive — reactivate to add jobs.') : undefined} onClick={() => { if (!isInactive) openPaymentDialog(selectedC.cleaner_id); }} data-testid={`split-cleaner-add-payment-${selectedC.cleaner_id}`}>
                            <CurrencyEur size={13} className="mr-1" /> {t('addPayment') || 'Add Payment'}
                          </Button>
                          {/* Phase 606 — Per-cleaner Active/Inactive toggle. */}
                          {isInactive ? (
                            <Button size="sm" variant="outline" className="rounded-none h-8 text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-50" disabled={cleanerStatusBusy} onClick={() => requestToggleCleanerStatus(selectedC)} data-testid={`split-cleaner-reactivate-${selectedC.cleaner_id}`}>
                              <ArrowCounterClockwise size={13} className="mr-1" /> {cleanerStatusBusy && pendingStatusCleaner?.cleaner_id === selectedC.cleaner_id ? (t('saving') || 'Saving…') : (t('reactivate') || 'Reactivate')}
                            </Button>
                          ) : (
                            <Button size="sm" variant="outline" className="rounded-none h-8 text-xs border-amber-300 text-amber-700 hover:bg-amber-50" disabled={cleanerStatusBusy} onClick={() => requestToggleCleanerStatus(selectedC)} data-testid={`split-cleaner-mark-inactive-${selectedC.cleaner_id}`}>
                              <Archive size={13} className="mr-1" /> {cleanerStatusBusy && pendingStatusCleaner?.cleaner_id === selectedC.cleaner_id ? (t('saving') || 'Saving…') : (t('markInactive') || 'Mark Inactive')}
                            </Button>
                          )}
                          {/* Phase 507 — Statement is now inline in the
                              Statement tab; the standalone "View Statement"
                              button + top-right PDF icons were duplicate
                              actions. Statement-area PDF export remains
                              available inside the Statement tab itself. */}
                          <Button variant="ghost" size="sm" className="h-8" onClick={() => openCleanerDialog(selectedC)} title={t('edit')}><PencilSimple size={15} /></Button>
                          <Button variant="ghost" size="sm" className="h-8 text-red-500" onClick={() => setDeletingCleaner(selectedC)} title={t('delete')}><Trash size={15} /></Button>
                          </>); })()}
                        </div>
                      </div>

                      {/* Data-mismatch banner */}
                      {mismatch && (
                        <div className="px-5 py-2 bg-amber-50 border-b border-amber-200 text-[12px] text-amber-800 flex items-center gap-2" data-testid="cleaner-data-mismatch">
                          <span className="font-bold">⚠ {t('dataMismatchDetected') || 'Data mismatch detected'}</span>
                          <span>
                            {t('expectedCount') || 'Expected'} {expectedJobs} · {t('shownCount') || 'shown'} {actualJobs}.
                          </span>
                          <button onClick={() => refreshCleanerDetail(selectedC.cleaner_id)} className="ml-auto underline">{t('refresh') || 'Refresh'}</button>
                        </div>
                      )}

                      {/* Stats row — Phase 712.1: simplified to 5 KPIs
                          (Jobs / Hours / Earned / Paid / Balance). The
                          old "Pending" and "Avg/Hour" tiles were dropped
                          per user request — payroll management only
                          needs Earned vs Paid. Balance = Earned − Paid,
                          coloured green when settled, red when owed. */}
                      {/* Phase 735.42 — Per user request, the primary
                          summary strip (Total Jobs / Hours / Earned /
                          Paid / Balance) was removed entirely. The
                          Monthly Payroll section below retains a
                          slimmed-down KPI row with only Jobs / Hours /
                          Earned. Backend calculations are unchanged. */}

                      {/* Phase 712.1 — Universal filter is now a simple
                          Month + Year selector (replaces the From/To
                          date pickers). When the user picks a month, the
                          backend filter becomes "first day of month →
                          last day of month" and scopes Jobs / Buildings
                          / Payments / Monthly Payroll consistently.
                          "All" clears the filter. */}
                      {(() => {
                        const cid = selectedC.cleaner_id;
                        const flt = getInlineFilter(cid);
                        // Derive month / year from the currently APPLIED filter so
                        // the dropdowns reflect what's actually active.
                        const appliedMonth = (flt.applied.from || '').slice(5, 7);   // "MM"
                        const appliedYear  = (flt.applied.from || '').slice(0, 4);   // "YYYY"
                        const draftMonth = flt.draftMonth ?? appliedMonth;
                        const draftYear  = flt.draftYear  ?? appliedYear;
                        const hasApplied = !!(flt.applied.from || flt.applied.to);

                        const MONTH_NAMES = language === 'el'
                          ? ['Ιανουάριος','Φεβρουάριος','Μάρτιος','Απρίλιος','Μάιος','Ιούνιος','Ιούλιος','Αύγουστος','Σεπτέμβριος','Οκτώβριος','Νοέμβριος','Δεκέμβριος']
                          : ['January','February','March','April','May','June','July','August','September','October','November','December'];
                        const currentYear = new Date().getFullYear();
                        const yearOptions = [];
                        for (let y = currentYear + 1; y >= currentYear - 5; y--) yearOptions.push(String(y));

                        const applyMonthYear = () => {
                          if (!draftMonth || !draftYear) {
                            // Treat as "All"
                            clearInlineStmtFilter(cid);
                            return;
                          }
                          const mm = String(draftMonth).padStart(2, '0');
                          const yy = String(draftYear);
                          // Compute first/last day of the selected month.
                          const lastDay = new Date(parseInt(yy, 10), parseInt(mm, 10), 0).getDate();
                          const from = `${yy}-${mm}-01`;
                          const to = `${yy}-${mm}-${String(lastDay).padStart(2, '0')}`;
                          setInlineFilter(cid, { from, to });
                          // Apply immediately — single click UX.
                          setTimeout(() => applyInlineStmtFilter(cid, { from, to }), 0);
                        };

                        return (
                          <div className="border-b border-zinc-200 bg-zinc-50/60 px-4 py-2 flex flex-wrap items-end gap-2" data-testid={`cleaner-filter-${cid}`}>
                            <div className="flex flex-col">
                              <Label className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold mb-1">{t('month') || 'Month'}</Label>
                              <Select value={draftMonth} onValueChange={(v) => setInlineFilter(cid, { draftMonth: v })}>
                                <SelectTrigger className="rounded-none h-8 text-xs w-36" data-testid={`cleaner-filter-month-${cid}`}><SelectValue placeholder={t('selectMonth') || 'Select month'} /></SelectTrigger>
                                <SelectContent>
                                  {MONTH_NAMES.map((nm, i) => (
                                    <SelectItem key={i} value={String(i + 1).padStart(2, '0')}>{nm}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="flex flex-col">
                              <Label className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold mb-1">{t('year') || 'Year'}</Label>
                              <Select value={draftYear} onValueChange={(v) => setInlineFilter(cid, { draftYear: v })}>
                                <SelectTrigger className="rounded-none h-8 text-xs w-28" data-testid={`cleaner-filter-year-${cid}`}><SelectValue placeholder={t('selectYear') || 'Select year'} /></SelectTrigger>
                                <SelectContent>
                                  {yearOptions.map((y) => (
                                    <SelectItem key={y} value={y}>{y}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <Button size="sm" className="rounded-none h-8 text-xs" onClick={applyMonthYear} disabled={!draftMonth || !draftYear} data-testid={`cleaner-filter-apply-${cid}`}>
                              {t('applyFilter') || 'Apply'}
                            </Button>
                            {hasApplied && (
                              <Button size="sm" variant="ghost" className="rounded-none h-8 text-xs" onClick={() => clearInlineStmtFilter(cid)} data-testid={`cleaner-filter-clear-${cid}`}>
                                {t('showAllMonths') || 'Show All'}
                              </Button>
                            )}
                            {hasApplied && (
                              <span className="ml-auto text-[11px] text-zinc-600 font-mono" data-testid={`cleaner-filter-active-period-${cid}`}>
                                {MONTH_NAMES[parseInt(appliedMonth, 10) - 1] || ''} {appliedYear}
                              </span>
                            )}
                          </div>
                        );
                      })()}

                      {/* Phase 735.44 — Invoice dashboard strip:
                          Total Invoiced / Total Paid / Outstanding.
                          Refreshed after every invoice mutation. */}
                      <CleanerInvoiceDashboard
                        cleanerId={selectedC.cleaner_id}
                        refreshKey={cleanerInvoicesRefreshKey}
                        axCfg={axCfg}
                        t={t}
                      />

                      {/* Tab nav */}
                      <div className="flex items-center gap-0 border-b border-zinc-200 bg-white px-4" data-testid="cleaner-tabs">
                        {[
                          { k: 'jobs',      label: t('jobs') || 'Jobs',                count: totalJobsCount },
                          { k: 'buildings', label: t('buildings') || 'Buildings',      count: totalBuildingsCount },
                          // Phase 735.44 — Cleaner Invoices (Accounts Payable).
                          { k: 'invoices',  label: t('invoices') || 'Invoices',        count: cleanerInvoiceCounts[selectedC.cleaner_id] ?? 0 },
                          { k: 'payments',  label: t('payments') || 'Payments',        count: paymentsList.length },
                          // Phase 735.46 — Statement ledger tab.
                          { k: 'ledger',    label: t('statement') || 'Statement',      count: null },
                          { k: 'statement', label: t('monthlyPayroll') || 'Monthly Payroll', count: (cleanerMonthlyPayroll[selectedC.cleaner_id]?.available_months || []).length },
                        ].map(({ k, label, count }) => {
                          const isActive = activeTab === k;
                          return (
                            <button
                              key={k}
                              onClick={() => setCleanerTab(selectedC.cleaner_id, k)}
                              data-testid={`cleaner-subtab-${k}`}
                              className={`px-4 py-2.5 text-sm border-b-2 transition-colors inline-flex items-center gap-2 ${
                                isActive
                                  ? 'border-zinc-900 text-zinc-900 font-semibold'
                                  : 'border-transparent text-zinc-500 hover:text-zinc-800'
                              }`}
                            >
                              {label}
                              <span className={`text-[10px] font-mono px-1.5 py-0.5 ${isActive ? 'bg-zinc-900 text-white' : 'bg-zinc-100 text-zinc-500'}`}>{count}</span>
                            </button>
                          );
                        })}
                      </div>

                      <div className="p-4">
                        {/* JOBS tab */}
                        {activeTab === 'jobs' && (
                          <div data-testid={`split-cleaner-jobs-${selectedC.cleaner_id}`} className="space-y-3">
                            {/* Phase 641 — Match the Statement tab's visual
                                language: compact period header, refined
                                table typography (tracking-wider uppercase
                                column heads, font-mono dates, tabular-nums
                                money), 3-line clamp on long client names,
                                soft hover row, and an accounting-style
                                tfoot summary that mirrors the Statement
                                totals row. */}
                            {(() => {
                              const flt = getInlineFilter(selectedC.cleaner_id);
                              const fromA = flt?.applied?.from;
                              const toA = flt?.applied?.to;
                              const totalJobs = jobsList.length;
                              const totalHours = jobsList.reduce((s, j) => s + (Number(j.hours_worked) || 0), 0);
                              const totalAmount = jobsList.reduce((s, j) => s + (Number(j.amount) || 0), 0);
                              const totalPaid = jobsList.reduce((s, j) => s + (Number(j.paid_amount) || 0), 0);
                              return (
                                <div className="text-[11px] text-zinc-500 tabular-nums flex items-center flex-wrap gap-x-3 gap-y-1" data-testid="jobs-inline-period">
                                  <span className="font-semibold text-zinc-700">{selectedC.name}</span>
                                  <span className="text-zinc-300">—</span>
                                  {fromA || toA ? (
                                    <span>{fmtDate(fromA) || '…'} → {fmtDate(toA) || '…'}</span>
                                  ) : (
                                    <span className="text-zinc-400">{t('allTime') || 'All time'}</span>
                                  )}
                                  <span className="text-zinc-300">·</span>
                                  <span>{totalJobs} {t('jobs') || 'jobs'}</span>
                                  <span className="text-zinc-300">·</span>
                                  <span>{totalHours.toFixed(2)} h</span>
                                  <span className="text-zinc-300">·</span>
                                  <span className="text-amber-700 font-semibold">€{totalAmount.toFixed(2)}</span>
                                  {totalPaid > 0.005 && totalPaid < totalAmount - 0.005 && (
                                    <>
                                      <span className="text-zinc-300">·</span>
                                      <span className="text-emerald-700">€{totalPaid.toFixed(2)} {t('paid') || 'paid'}</span>
                                    </>
                                  )}
                                </div>
                              );
                            })()}
                            <table className="w-full text-xs bg-white border border-zinc-200">
                              <thead>
                                <tr className="bg-zinc-50 border-b border-zinc-200 text-[10px] text-zinc-500 uppercase tracking-wider">
                                  <th className="text-left px-3 py-2 w-24">{t('date')}</th>
                                  <th className="text-left px-3 py-2">{t('client') || 'Client'}</th>
                                  <th className="text-right px-3 py-2 w-20">{t('hoursWorked') || t('totalHours')}</th>
                                  <th className="text-right px-3 py-2 w-24">{t('amount')}</th>
                                  {/* Phase 735.46 — Status column removed.
                                      Payment status now lives on Invoices, not
                                      individual Jobs. */}
                                  <th className="text-left px-3 py-2 w-[280px]">{t('notes') || 'Notes'}</th>
                                </tr>
                              </thead>
                              <tbody>
                                {jobsList.length === 0 ? (
                                  <tr><td colSpan={5} className="text-center py-12 text-zinc-400">
                                    <Broom size={28} className="mx-auto mb-2 text-zinc-300" />
                                    {t('noJobsYet') || 'No jobs found'}
                                  </td></tr>
                                ) : jobsList.map((j, i) => {
                                  const note = (j.notes || '').trim();
                                  return (
                                  <tr key={i} className="border-b border-zinc-100 hover:bg-zinc-50/60 align-top">
                                    <td className="px-3 py-1.5 font-mono text-zinc-700 whitespace-nowrap">{fmtDate(j.date)}</td>
                                    <td className="px-3 py-1.5 text-zinc-800">
                                      <div className="line-clamp-2 leading-snug break-words" title={j.client_name || ''}>
                                        {j.client_name || '—'}
                                      </div>
                                    </td>
                                    <td className="px-3 py-1.5 text-right font-mono tabular-nums text-zinc-700 whitespace-nowrap">{(Number(j.hours_worked) || 0).toFixed(2)}</td>
                                    <td className="px-3 py-1.5 text-right font-mono tabular-nums font-semibold text-amber-700 whitespace-nowrap">€{(Number(j.amount) || 0).toFixed(2)}</td>
                                    <td className="px-3 py-1.5 text-zinc-600" title={note}>
                                      {note ? (
                                        <div className="text-[11px] leading-snug line-clamp-2 break-words">{note}</div>
                                      ) : (
                                        <span className="text-zinc-300">—</span>
                                      )}
                                    </td>
                                  </tr>
                                  );
                                })}
                              </tbody>
                              {jobsList.length > 0 && (
                                <tfoot>
                                  <tr className="border-t-2 border-zinc-300 bg-zinc-50 text-[10px] uppercase tracking-wider font-semibold text-zinc-600">
                                    <td colSpan={2} className="px-3 py-2 text-right">{t('total')}</td>
                                    <td className="px-3 py-2 text-right font-mono tabular-nums text-zinc-800">{jobsList.reduce((s, j) => s + (Number(j.hours_worked) || 0), 0).toFixed(2)}</td>
                                    <td className="px-3 py-2 text-right font-mono tabular-nums text-amber-700">€{jobsList.reduce((s, j) => s + (Number(j.amount) || 0), 0).toFixed(2)}</td>
                                    <td></td>
                                  </tr>
                                </tfoot>
                              )}
                            </table>
                          </div>
                        )}

                        {/* BUILDINGS tab */}
                        {activeTab === 'buildings' && (
                          <div data-testid={`split-cleaner-buildings-${selectedC.cleaner_id}`} className="space-y-3">
                            {/* Phase 641 — Same Statement-style header
                                strip + refined table for the Buildings
                                view. Building names render as a soft
                                two-line clamp (with full text in title
                                tooltip) so long names like "Κυπριακή
                                Εταιρεία Πιστοποίησης ΛΤΔ" don't blow up
                                the row height. */}
                            {(() => {
                              const flt = getInlineFilter(selectedC.cleaner_id);
                              const fromA = flt?.applied?.from;
                              const toA = flt?.applied?.to;
                              const totalDays = buildingsList.length;
                              const totalEntries = buildingsList.reduce((s, d) => s + (d.entry_count || 0), 0);
                              const totalAmount = buildingsList.reduce((s, d) => s + (d.amount || 0), 0);
                              const totalPaid = buildingsList.reduce((s, d) => s + (Number(d.paid_amount) || 0), 0);
                              return (
                                <div className="text-[11px] text-zinc-500 tabular-nums flex items-center flex-wrap gap-x-3 gap-y-1" data-testid="buildings-inline-period">
                                  <span className="font-semibold text-zinc-700">{selectedC.name}</span>
                                  <span className="text-zinc-300">—</span>
                                  {fromA || toA ? (
                                    <span>{fmtDate(fromA) || '…'} → {fmtDate(toA) || '…'}</span>
                                  ) : (
                                    <span className="text-zinc-400">{t('allTime') || 'All time'}</span>
                                  )}
                                  <span className="text-zinc-300">·</span>
                                  <span>{totalDays} {totalDays === 1 ? (t('day') || 'day') : (t('days') || 'days')}</span>
                                  <span className="text-zinc-300">·</span>
                                  <span>{totalEntries} {t('entries') || 'entries'}</span>
                                  <span className="text-zinc-300">·</span>
                                  <span className="text-amber-700 font-semibold">€{totalAmount.toFixed(2)}</span>
                                  {totalPaid > 0.005 && totalPaid < totalAmount - 0.005 && (
                                    <>
                                      <span className="text-zinc-300">·</span>
                                      <span className="text-emerald-700">€{totalPaid.toFixed(2)} {t('paid') || 'paid'}</span>
                                    </>
                                  )}
                                </div>
                              );
                            })()}
                            <table className="w-full text-xs bg-white border border-zinc-200">
                              <thead>
                                <tr className="bg-zinc-50 border-b border-zinc-200 text-[10px] text-zinc-500 uppercase tracking-wider">
                                  <th className="text-left px-3 py-2 w-24">{t('date')}</th>
                                  <th className="text-left px-3 py-2">{t('buildings') || 'Buildings'}</th>
                                  <th className="text-right px-3 py-2 w-20">{t('entries') || 'Entries'}</th>
                                  <th className="text-right px-3 py-2 w-24">{t('amount')}</th>
                                  {/* Phase 735.46 — Status column removed. */}
                                  <th className="text-left px-3 py-2 w-[280px]">{t('notes') || 'Notes'}</th>
                                </tr>
                              </thead>
                              <tbody>
                                {buildingsList.length === 0 ? (
                                  <tr><td colSpan={5} className="text-center py-12 text-zinc-400">
                                    <Buildings size={28} className="mx-auto mb-2 text-zinc-300" />
                                    {t('noBuildingsCleaning') || 'No building cleaning records'}
                                  </td></tr>
                                ) : buildingsList.map((d, i) => {
                                  const note = (d.notes || '').trim();
                                  const names = (d.buildings || []).map(b => b.building_name).filter(Boolean);
                                  const joined = names.join(', ');
                                  return (
                                  <tr key={i} className="border-b border-zinc-100 hover:bg-zinc-50/60 align-top">
                                    <td className="px-3 py-1.5 font-mono text-zinc-700 whitespace-nowrap">{fmtDate(d.date)}</td>
                                    <td className="px-3 py-1.5 text-zinc-800">
                                      <div className="leading-snug line-clamp-2 break-words" title={joined}>{joined || '—'}</div>
                                    </td>
                                    <td className="px-3 py-1.5 text-right font-mono tabular-nums text-zinc-700 whitespace-nowrap">{d.entry_count || 0}</td>
                                    <td className="px-3 py-1.5 text-right font-mono tabular-nums font-semibold text-amber-700 whitespace-nowrap">€{(d.amount || 0).toFixed(2)}</td>
                                    <td className="px-3 py-1.5 text-zinc-600" title={note}>
                                      {note ? (
                                        <div className="text-[11px] leading-snug line-clamp-2 break-words">{note}</div>
                                      ) : (
                                        <span className="text-zinc-300">—</span>
                                      )}
                                    </td>
                                  </tr>
                                  );
                                })}
                              </tbody>
                              {buildingsList.length > 0 && (
                                <tfoot>
                                  <tr className="border-t-2 border-zinc-300 bg-zinc-50 text-[10px] uppercase tracking-wider font-semibold text-zinc-600">
                                    <td colSpan={2} className="px-3 py-2 text-right">{t('total')}</td>
                                    <td className="px-3 py-2 text-right font-mono tabular-nums text-zinc-800">{buildingsList.reduce((s, d) => s + (d.entry_count || 0), 0)}</td>
                                    <td className="px-3 py-2 text-right font-mono tabular-nums text-amber-700">€{buildingsList.reduce((s, d) => s + (d.amount || 0), 0).toFixed(2)}</td>
                                    <td></td>
                                  </tr>
                                </tfoot>
                              )}
                            </table>
                          </div>
                        )}

                        {/* Phase 735.44 — INVOICES tab. Cleaner
                            Accounts-Payable ledger. Voided invoices
                            are hidden by default; toggle "Show voided"
                            to include them. */}
                        {activeTab === 'invoices' && (
                          <CleanerInvoicesTab
                            cleanerId={selectedC.cleaner_id}
                            language={language}
                            t={t}
                            axCfg={axCfg}
                            onChange={() => {
                              setCleanerInvoicesRefreshKey((k) => k + 1);
                              fetchCleaners();
                              // Cheap count refresh so the tab badge
                              // stays accurate after Create/Void.
                              (async () => {
                                try {
                                  const res = await axios.get(
                                    `${API}/cleaning/cleaners/${selectedC.cleaner_id}/invoices?include_voided=false`,
                                    axCfg,
                                  );
                                  setCleanerInvoiceCounts((prev) => ({ ...prev, [selectedC.cleaner_id]: res.data?.invoices?.length ?? 0 }));
                                } catch { /* silent */ }
                              })();
                            }}
                          />
                        )}

                        {/* Phase 735.46 — STATEMENT LEDGER tab. Running-
                            balance ledger merging invoices (debit) and
                            payments (credit) chronologically. */}
                        {activeTab === 'ledger' && (
                          <CleanerStatementTab
                            cleanerId={selectedC.cleaner_id}
                            axCfg={axCfg}
                            t={t}
                          />
                        )}

                        {/* PAYMENTS tab — Phase 712.1: simplified.
                            Allocation column removed; Payroll Month
                            shown instead. */}
                        {activeTab === 'payments' && (
                          <div data-testid={`split-cleaner-payments-${selectedC.cleaner_id}`}>
                            <table className="w-full text-sm bg-white border border-zinc-200">
                              <thead><tr className="bg-zinc-50 border-b text-[10px] text-zinc-500 uppercase tracking-wider">
                                <th className="text-left px-3 py-2">{t('paymentDate') || 'Payment Date'}</th>
                                <th className="text-right px-3 py-2">{t('amount')}</th>
                                <th className="text-left px-3 py-2">{t('payrollMonth') || 'Payroll Month'}</th>
                                <th className="text-left px-3 py-2">{t('notes')}</th>
                                <th className="w-24 px-3 py-2"></th>
                              </tr></thead>
                              <tbody>
                                {paymentsList.length === 0 ? (
                                  <tr><td colSpan={5} className="text-center py-12 text-zinc-400">
                                    <CurrencyEur size={28} className="mx-auto mb-2 text-zinc-300" />
                                    {t('noPaymentsRecorded') || 'No payments recorded'}
                                  </td></tr>
                                ) : paymentsList.map(p => {
                                  const amt = Number(p.amount || 0);
                                  // Derive a display label for the
                                  // payroll month. Fallback to the
                                  // payment date's month when legacy
                                  // payments lack the new field.
                                  const pm = (p.payroll_month || (p.date || '').slice(0, 7)) || '';
                                  let pmLabel = '—';
                                  if (pm) {
                                    const [yy, mm] = pm.split('-');
                                    const monthsEn = ['January','February','March','April','May','June','July','August','September','October','November','December'];
                                    const monthsEl = ['Ιανουάριος','Φεβρουάριος','Μάρτιος','Απρίλιος','Μάιος','Ιούνιος','Ιούλιος','Αύγουστος','Σεπτέμβριος','Οκτώβριος','Νοέμβριος','Δεκέμβριος'];
                                    const mi = parseInt(mm, 10) - 1;
                                    if (mi >= 0 && mi < 12) {
                                      pmLabel = `${(language === 'el' ? monthsEl : monthsEn)[mi]} ${yy}`;
                                    }
                                  }
                                  return (
                                  <tr key={p.payment_id} className="border-b border-zinc-100 hover:bg-zinc-50">
                                    <td className="px-3 py-2 font-mono">{fmtDate(p.date)}</td>
                                    <td className="px-3 py-2 text-right font-mono text-emerald-700 font-semibold">€{amt.toFixed(2)}</td>
                                    <td className="px-3 py-2">
                                      <span className="text-xs font-semibold text-zinc-700" data-testid={`pay-payroll-month-${p.payment_id}`}>{pmLabel}</span>
                                    </td>
                                    <td className="px-3 py-2 text-zinc-600">{p.notes || '-'}</td>
                                    <td className="px-3 py-2">
                                      <div className="flex items-center gap-1 justify-end">
                                        <button onClick={() => openPaymentDialog(selectedC.cleaner_id, p)} className="text-zinc-400 hover:text-zinc-900" title={t('edit')} data-testid={`split-pay-edit-${p.payment_id}`}><PencilSimple size={14} /></button>
                                        <button onClick={() => deleteClnrPayment(selectedC.cleaner_id, p.payment_id)} className="text-zinc-400 hover:text-red-600" title={t('delete')} data-testid={`split-pay-delete-${p.payment_id}`}><Trash size={14} /></button>
                                      </div>
                                    </td>
                                  </tr>
                                ); })}
                              </tbody>
                            </table>
                          </div>
                        )}

                        {/* STATEMENT tab — Phase 712.1: Monthly Payroll
                            (single-month view). The universal Month/Year
                            filter at the top picks which month to show.
                            When no filter is active we default to the
                            most-recent month that has data, so the user
                            always sees something. No allocation, no
                            unallocated section — payments are simply
                            grouped by their `payroll_month` field. */}
                        {activeTab === 'statement' && (() => {
                          const cid = selectedC.cleaner_id;
                          const payroll = cleanerMonthlyPayroll[cid];
                          const isLoading = monthlyPayrollLoading[cid];
                          const monthLabel = (m) => language === 'el' ? (m.month_label_el || m.month_label_en) : m.month_label_en;

                          // The endpoint either returns `month` (single
                          // selected month) when a month filter is on,
                          // or `months[]` (all months) otherwise. Pick
                          // the most recent month with data as the
                          // default for the "All time" case so the UI
                          // never shows nothing when data exists.
                          const flt = getInlineFilter(cid);
                          const hasFilter = !!(flt.applied.from || flt.applied.to);
                          const month = payroll?.month || (payroll?.months && payroll.months[0]) || null;
                          const availableMonths = payroll?.available_months || [];

                          const labelText = month ? monthLabel(month) : '';

                          return (
                            <div data-testid={`split-cleaner-stmt-${cid}`} className="space-y-4">
                              {/* Header */}
                              <div className="flex items-center justify-between flex-wrap gap-3" data-testid="payroll-header">
                                <div>
                                  <div className="text-xs uppercase tracking-wider text-zinc-500 font-semibold">{t('monthlyPayroll') || 'Monthly Payroll'}</div>
                                  <div className="text-2xl font-bold text-zinc-900 mt-0.5" data-testid="payroll-current-label">{labelText || '—'}</div>
                                </div>
                                {!hasFilter && availableMonths.length > 1 && (
                                  <div className="flex items-center gap-2">
                                    <Label className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">{t('jumpToMonth') || 'Jump to month'}</Label>
                                    <Select value={month?.month_key || ''} onValueChange={(mk) => {
                                      // Quick jump: apply that month via the universal filter.
                                      if (!mk) return;
                                      const [yy, mm] = mk.split('-');
                                      const lastDay = new Date(parseInt(yy, 10), parseInt(mm, 10), 0).getDate();
                                      const from = `${yy}-${mm}-01`;
                                      const to = `${yy}-${mm}-${String(lastDay).padStart(2, '0')}`;
                                      setInlineFilter(cid, { from, to, draftMonth: mm, draftYear: yy });
                                      applyInlineStmtFilter(cid, { from, to });
                                    }}>
                                      <SelectTrigger className="rounded-none h-8 text-xs w-44" data-testid="payroll-month-jumper"><SelectValue placeholder={t('selectMonth') || 'Select month'} /></SelectTrigger>
                                      <SelectContent>
                                        {availableMonths.map((am) => (
                                          <SelectItem key={am.month_key} value={am.month_key}>{language === 'el' ? am.label_el : am.label_en}</SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  </div>
                                )}
                              </div>

                              {/* Loading / empty states */}
                              {isLoading && !payroll && (
                                <div className="text-center text-zinc-400 text-xs py-6" data-testid="payroll-loading">{t('loading') || 'Loading…'}</div>
                              )}
                              {!isLoading && !month && (
                                <div className="text-center text-zinc-400 text-xs py-8 border border-dashed border-zinc-200" data-testid="payroll-empty">{t('noData') || 'No data for the selected period.'}</div>
                              )}

                              {/* Single-month payroll card */}
                              {month && (
                                <div className="space-y-4" data-testid={`payroll-month-${month.month_key}`}>
                                  {/* Month KPI strip — Phase 735.42.
                                      Per user request, "Total Paid" and
                                      "Balance" tiles have been removed
                                      from this row. Payment tracking
                                      lives in the Payments tab; this
                                      strip focuses on the payroll
                                      period at a glance. Backend
                                      calculations for month.total_paid /
                                      balance are untouched — the values
                                      are simply not surfaced here. */}
                                  <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-zinc-200 border border-zinc-200" data-testid="payroll-month-kpis">
                                    <div className="bg-white px-3 py-2">
                                      <div className="text-[9px] uppercase tracking-wider text-zinc-400 font-semibold">{t('totalJobs') || 'Total Jobs'}</div>
                                      <div className="font-mono font-semibold text-zinc-800 tabular-nums text-base">{month.jobs_count}</div>
                                    </div>
                                    <div className="bg-white px-3 py-2">
                                      <div className="text-[9px] uppercase tracking-wider text-zinc-400 font-semibold">{t('totalHours') || 'Total Hours'}</div>
                                      <div className="font-mono font-semibold text-zinc-800 tabular-nums text-base">{Number(month.total_hours || 0).toFixed(1)}</div>
                                    </div>
                                    <div className="bg-white px-3 py-2">
                                      <div className="text-[9px] uppercase tracking-wider text-zinc-400 font-semibold">{t('totalEarned') || 'Total Earned'}</div>
                                      <div className="font-mono font-semibold text-zinc-800 tabular-nums text-base" data-testid={`payroll-month-earned-${month.month_key}`}>€{month.total_earned.toFixed(2)}</div>
                                    </div>
                                  </div>

                                  {/* Jobs & Buildings — Phase 712.3:
                                      grouped by date. Multiple BUILDING
                                      cleanings on the same date collapse
                                      into one row labelled "Buildings"
                                      with summed amount; the chevron
                                      expands to reveal the individual
                                      building lines. JOB rows always
                                      render individually (different
                                      clients are not interchangeable).
                                      Totals are NEVER recomputed from
                                      grouped rows — `month.total_earned`
                                      comes straight from the backend so
                                      this is presentation-only. */}
                                  <div>
                                    <div className="flex items-center gap-2 mb-1.5">
                                      <span className="text-[10px] uppercase tracking-wider font-semibold text-zinc-600">{t('jobsAndBuildings') || 'Jobs & Buildings'}</span>
                                      <span className="flex-1 border-t border-zinc-200"></span>
                                    </div>
                                    {month.earnings.length === 0 ? (
                                      <div className="text-[11px] text-zinc-400 italic py-2 px-2 border border-dashed border-zinc-200">{t('noJobsThisMonth') || 'No jobs recorded for this month.'}</div>
                                    ) : (() => {
                                      // Build display rows. Walk earnings
                                      // in date order; collapse runs of
                                      // BUILDING entries that share the
                                      // same date.
                                      const sortedEarnings = [...month.earnings].sort((a, b) => (a.date || '').localeCompare(b.date || ''));
                                      const groups = []; // { isGroup, date, kind, reference, amount, subRows[], key }
                                      const byKey = new Map();
                                      for (const e of sortedEarnings) {
                                        if (e.kind === 'building') {
                                          const k = `bld|${e.date}`;
                                          if (byKey.has(k)) {
                                            const g = byKey.get(k);
                                            g.subRows.push(e);
                                            g.amount += Number(e.amount || 0);
                                          } else {
                                            const g = { isGroup: true, date: e.date, kind: 'building', reference: '', amount: Number(e.amount || 0), subRows: [e], key: k };
                                            byKey.set(k, g);
                                            groups.push(g);
                                          }
                                        } else {
                                          groups.push({ isGroup: false, date: e.date, kind: e.kind, reference: e.reference, notes: e.notes, amount: Number(e.amount || 0), subRows: [e], key: `job|${e.date}|${e.client_id || e.reference}|${groups.length}` });
                                        }
                                      }
                                      // Finalise grouped buildings:
                                      // when only 1 building exists for a
                                      // date, render flat (no grouping
                                      // chevron) and use its real name
                                      // so the screen stays readable.
                                      for (const g of groups) {
                                        if (g.kind === 'building' && g.subRows.length === 1) {
                                          g.isGroup = false;
                                          g.reference = g.subRows[0].reference;
                                          g.notes = g.subRows[0].notes;
                                        } else if (g.kind === 'building') {
                                          g.reference = t('buildings') || 'Buildings';
                                        }
                                        g.amount = Math.round(g.amount * 100) / 100;
                                      }

                                      const isExpanded = (g) => !!payrollGroupExpanded[`${cid}|${month.month_key}|${g.key}`];
                                      const toggle = (g) => setPayrollGroupExpanded(prev => ({
                                        ...prev,
                                        [`${cid}|${month.month_key}|${g.key}`]: !prev[`${cid}|${month.month_key}|${g.key}`],
                                      }));

                                      return (
                                        <table className="w-full text-xs bg-white border border-zinc-200" data-testid={`payroll-month-earnings-${month.month_key}`}>
                                          <thead>
                                            <tr className="bg-zinc-50 border-b border-zinc-200 text-[10px] text-zinc-500 uppercase tracking-wider">
                                              <th className="text-left px-3 py-1.5 w-24">{t('date') || 'Date'}</th>
                                              <th className="text-left px-3 py-1.5">{t('reference') || 'Reference'}</th>
                                              <th className="text-right px-3 py-1.5 w-28">{t('amount') || 'Amount'}</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {groups.map((g) => {
                                              const expanded = g.isGroup && isExpanded(g);
                                              const groupTestId = g.isGroup ? `payroll-group-${g.key}` : undefined;
                                              return (
                                                <React.Fragment key={g.key}>
                                                  <tr
                                                    className={`border-b border-zinc-100 last:border-b-0 ${g.isGroup ? 'cursor-pointer hover:bg-zinc-50' : 'hover:bg-zinc-50/60'}`}
                                                    onClick={g.isGroup ? () => toggle(g) : undefined}
                                                    data-testid={groupTestId}
                                                  >
                                                    <td className="px-3 py-1.5 font-mono text-zinc-700 whitespace-nowrap">
                                                      <span className="inline-flex items-center gap-1">
                                                        {g.isGroup ? (
                                                          expanded
                                                            ? <CaretDown size={12} className="text-zinc-400" />
                                                            : <CaretRight size={12} className="text-zinc-400" />
                                                        ) : (
                                                          <span className="inline-block w-3" />
                                                        )}
                                                        {fmtDate(g.date)}
                                                      </span>
                                                    </td>
                                                    <td className="px-3 py-1.5 text-zinc-800">
                                                      <div className="truncate max-w-[520px]" title={g.reference}>
                                                        {g.reference || '—'}
                                                        {g.isGroup && (
                                                          <span className="ml-2 text-[10px] text-zinc-400 font-normal">
                                                            ({g.subRows.length} {t('items') || 'items'})
                                                          </span>
                                                        )}
                                                      </div>
                                                      {!g.isGroup && g.notes && (
                                                        <div className="text-[10px] text-zinc-400 truncate max-w-[520px]" title={g.notes}>{g.notes}</div>
                                                      )}
                                                    </td>
                                                    <td className="px-3 py-1.5 text-right font-mono tabular-nums text-zinc-800 font-semibold" data-testid={g.isGroup ? `payroll-group-amount-${g.key}` : undefined}>€{g.amount.toFixed(2)}</td>
                                                  </tr>
                                                  {expanded && g.subRows.map((sr, si) => (
                                                    <tr key={`${g.key}-sub-${si}`} className="border-b border-zinc-100 last:border-b-0 bg-zinc-50/40" data-testid={`payroll-group-sub-${g.key}-${si}`}>
                                                      <td className="px-3 py-1 font-mono text-zinc-400 whitespace-nowrap text-[11px]">
                                                        <span className="inline-block w-5" />
                                                      </td>
                                                      <td className="px-3 py-1 text-zinc-600 text-[11px]">
                                                        <div className="truncate max-w-[520px]" title={sr.reference}>↳ {sr.reference || '—'}</div>
                                                        {sr.notes && (
                                                          <div className="text-[10px] text-zinc-400 truncate max-w-[520px]" title={sr.notes}>{sr.notes}</div>
                                                        )}
                                                      </td>
                                                      <td className="px-3 py-1 text-right font-mono tabular-nums text-zinc-600 text-[11px]">€{Number(sr.amount || 0).toFixed(2)}</td>
                                                    </tr>
                                                  ))}
                                                </React.Fragment>
                                              );
                                            })}
                                          </tbody>
                                          <tfoot>
                                            <tr className="border-t-2 border-zinc-300 bg-zinc-50 text-[10px] uppercase tracking-wider font-semibold text-zinc-600">
                                              <td colSpan={2} className="px-3 py-1.5 text-right">{t('totalEarned') || 'Total Earned'}</td>
                                              <td className="px-3 py-1.5 text-right font-mono tabular-nums text-zinc-800">€{month.total_earned.toFixed(2)}</td>
                                            </tr>
                                          </tfoot>
                                        </table>
                                      );
                                    })()}
                                  </div>

                                  {/* Payments for this payroll month */}
                                  <div>
                                    <div className="flex items-center gap-2 mb-1.5">
                                      <span className="text-[10px] uppercase tracking-wider font-semibold text-zinc-600">
                                        {(t('paymentsForMonthWork') || 'Payments for {month} Work').replace('{month}', labelText)}
                                      </span>
                                      <span className="flex-1 border-t border-zinc-200"></span>
                                    </div>
                                    {month.payments.length === 0 ? (
                                      <div className="text-[11px] text-zinc-400 italic py-2 px-2 border border-dashed border-zinc-200">{t('noPaymentsThisMonthWork') || 'No payments have been recorded against this payroll month yet.'}</div>
                                    ) : (
                                      <table className="w-full text-xs bg-white border border-zinc-200" data-testid={`payroll-month-payments-${month.month_key}`}>
                                        <thead>
                                          <tr className="bg-zinc-50 border-b border-zinc-200 text-[10px] text-zinc-500 uppercase tracking-wider">
                                            <th className="text-left px-3 py-1.5 w-28">{t('paymentDate') || 'Payment Date'}</th>
                                            <th className="text-left px-3 py-1.5">{t('reference') || 'Reference'}</th>
                                            <th className="text-right px-3 py-1.5 w-28">{t('amount') || 'Amount'}</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {month.payments.map((p, i) => (
                                            <tr key={`${month.month_key}-p-${i}`} className="border-b border-zinc-100 last:border-b-0 hover:bg-zinc-50/60">
                                              <td className="px-3 py-1.5 font-mono text-zinc-600 whitespace-nowrap" title={t('paymentDateInfoOnly') || 'Payment date is informational only'}>{fmtDate(p.date)}</td>
                                              <td className="px-3 py-1.5 text-zinc-800">
                                                <span className="font-mono text-zinc-700">{p.reference || '—'}</span>
                                                {p.notes && (
                                                  <div className="text-[10px] text-zinc-400 truncate max-w-[520px]" title={p.notes}>{p.notes}</div>
                                                )}
                                              </td>
                                              <td className="px-3 py-1.5 text-right font-mono tabular-nums text-emerald-700 font-semibold">€{Number(p.amount || 0).toFixed(2)}</td>
                                            </tr>
                                          ))}
                                        </tbody>
                                        <tfoot>
                                          <tr className="border-t-2 border-zinc-300 bg-zinc-50 text-[10px] uppercase tracking-wider font-semibold text-zinc-600">
                                            <td colSpan={2} className="px-3 py-1.5 text-right">{t('totalPaid') || 'Total Paid'}</td>
                                            <td className="px-3 py-1.5 text-right font-mono tabular-nums text-emerald-700">€{month.total_paid.toFixed(2)}</td>
                                          </tr>
                                        </tfoot>
                                      </table>
                                    )}
                                  </div>
                                </div>
                              )}

                              <div className="flex items-center justify-end gap-2 pt-1">
                                <Button size="sm" variant="outline" className="rounded-none h-8 text-xs" onClick={() => {
                                  const f = getInlineFilter(cid);
                                  const params = { show_buildings: 'true' };
                                  if (f.applied.from) params.date_from = f.applied.from;
                                  if (f.applied.to) params.date_to = f.applied.to;
                                  openPdfInBrowser(`/cleaning/cleaners/${cid}/statement/pdf`, params);
                                }} data-testid={`split-cleaner-stmt-view-pdf-${cid}`}>
                                  <Eye size={13} className="mr-1" /> {t('viewPdf') || 'View PDF'}
                                </Button>
                              </div>
                            </div>
                          );
                        })()}


                      </div>
                    </div>
                  )}
                </div>
              </div>
              );
            })()}
          </div>
        )}

        {/* ============ CLIENTS TAB ============ */}
        {activeTab === 'clients' && (
          <div data-testid="clients-tab">
            <div className="flex flex-wrap justify-between items-center mb-4 gap-3">
              <div className="flex items-center gap-3 flex-wrap">
                <p className="text-sm text-zinc-500">{filteredClients.length} {t('clients').toLowerCase()}</p>
                <div className="relative"><MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400" /><Input value={clientSearch} onChange={e => setClientSearch(e.target.value)} placeholder={t('search')} className="rounded-none pl-8 h-8 w-48 text-xs" data-testid="client-search" /></div>
                <div className="flex items-center gap-1 bg-white border border-zinc-200 p-0.5 flex-nowrap overflow-x-auto max-w-full">
                  <button onClick={() => setClientTypeFilter('all')} className={`px-2.5 py-1 text-xs font-medium transition whitespace-nowrap shrink-0 ${clientTypeFilter === 'all' ? 'bg-zinc-900 text-white' : 'text-zinc-500 hover:bg-zinc-100'}`} data-testid="client-filter-all">{t('all')}</button>
                  {categories.map(c => {
                    const val = c.name.toLowerCase().replace(/\s+/g, '_');
                    return <button key={c.category_id} onClick={() => setClientTypeFilter(val)} className={`px-2.5 py-1 text-xs font-medium transition whitespace-nowrap shrink-0 ${clientTypeFilter === val ? 'bg-zinc-900 text-white' : 'text-zinc-500 hover:bg-zinc-100'}`} data-testid={`client-filter-${val}`}>{language === 'el' ? (c.name_el || c.name) : c.name}</button>;
                  })}
                </div>
                {/* Phase 477 — Manage Categories (replaces the standalone tab). */}
                <button
                  type="button"
                  onClick={() => setCategoriesDialog(true)}
                  className="inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-900 border border-zinc-200 bg-white px-2 py-1 hover:border-zinc-400 transition-colors"
                  data-testid="manage-categories-btn"
                  title={t('manageCategories') || 'Manage categories'}
                >
                  <Tag size={12} /> {t('manageCategories') || 'Manage'}
                </button>
              </div>
              <div className="flex items-center gap-2">
                {/* Phase 471 — Split / Accordion view toggle. */}
                <div className="hidden md:flex items-center gap-0 border border-zinc-200 bg-white" data-testid="clients-view-mode">
                  <button
                    onClick={() => setClientsViewMode('split')}
                    className={`px-2.5 py-1 text-[11px] font-medium uppercase tracking-wider transition ${clientsViewMode === 'split' ? 'bg-zinc-900 text-white' : 'text-zinc-500 hover:bg-zinc-100'}`}
                    data-testid="clients-view-split"
                    title={t('splitView') || 'Split view'}
                  >
                    {t('splitView') || 'Split'}
                  </button>
                  <button
                    onClick={() => setClientsViewMode('accordion')}
                    className={`px-2.5 py-1 text-[11px] font-medium uppercase tracking-wider transition ${clientsViewMode === 'accordion' ? 'bg-zinc-900 text-white' : 'text-zinc-500 hover:bg-zinc-100'}`}
                    data-testid="clients-view-accordion"
                    title={t('listView') || 'List view'}
                  >
                    {t('listView') || 'List'}
                  </button>
                </div>
                <Button onClick={() => openClientDialog()} size="sm" className="rounded-none" data-testid="add-client-btn"><Plus size={16} className="mr-1" /> {t('addClient')}</Button>
              </div>
            </div>
            {/* Phase 263 — Bulk action bar (clients). */}
            {selectedClientIds.size > 0 && (
              <div className="flex items-center justify-between mb-3 px-3 py-2 bg-zinc-900 text-white" data-testid="clients-bulk-bar">
                <div className="text-xs">
                  <strong>{selectedClientIds.size}</strong> {t('selected') || 'selected'}
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => clearSelection('client')} className="text-xs text-zinc-300 hover:text-white px-2 py-1" data-testid="clients-clear-selection">{t('clear') || 'Clear'}</button>
                  <Button size="sm" variant="destructive" className="rounded-none h-7 text-xs" onClick={() => setBulkDeleteConfirm({ kind: 'client', ids: [...selectedClientIds] })} data-testid="clients-bulk-delete-btn">
                    <Trash size={14} className="mr-1" /> {t('deleteSelected') || 'Delete Selected'}
                  </Button>
                </div>
              </div>
            )}
            {filteredClients.length > 0 && (
              <div className="flex items-center gap-2 mb-2 text-xs text-zinc-500">
                <input
                  type="checkbox"
                  className="accent-zinc-900"
                  checked={selectedClientIds.size > 0 && selectedClientIds.size === filteredClients.length}
                  onChange={(e) => e.target.checked ? setAllSelected('client', filteredClients) : clearSelection('client')}
                  data-testid="clients-select-all"
                />
                <span>{t('selectAll') || 'Select all'}</span>
              </div>
            )}

            {/* Phase 102 — Totals strip when a specific client type is selected (Bonus, Office, House, etc.).
                Provides quick visibility of revenue, cost and profit for the active filter. */}
            {clientTypeFilter !== 'all' && filteredClients.length > 0 && (() => {
              // Phase 614 — Operational totals: invoiced (canonical IC-* only)
              // and operational profit (invoiced − cleaner cost) so the strip
              // reconciles with the right-pane KPI cards.
              const totals = filteredClients.reduce((acc, c) => ({
                jobs: acc.jobs + Number(c.total_jobs || 0),
                hours: acc.hours + Number(c.total_hours || 0),
                cost: acc.cost + Number(c.total_cost || 0),
              }), { jobs: 0, hours: 0, cost: 0 });
              const label = getClientTypeLabel(clientTypeFilter);
              return (
                <div className="flex items-stretch w-full border border-zinc-200 divide-x divide-zinc-200 mb-4" data-testid={`clients-totals-strip-${clientTypeFilter}`}>
                  <div className="flex-1 min-w-[140px] px-4 py-2">
                    <div className="text-[9px] font-semibold uppercase tracking-wider text-zinc-400">{label}</div>
                    <div className="text-xs text-zinc-500 font-mono">{filteredClients.length} {filteredClients.length === 1 ? t('client') : t('clients').toLowerCase()}</div>
                  </div>
                  <div className="flex-1 min-w-[140px] basis-0 px-4 py-2" data-testid="clients-total-jobs">
                    <div className="text-[9px] font-semibold uppercase tracking-wider text-zinc-500">{t('totalJobs')}</div>
                    <div className="text-sm font-mono font-bold tabular-nums text-zinc-800">{totals.jobs}</div>
                  </div>
                  <div className="flex-1 min-w-[140px] basis-0 px-4 py-2" data-testid="clients-total-hours">
                    <div className="text-[9px] font-semibold uppercase tracking-wider text-zinc-500">{t('totalHours')}</div>
                    <div className="text-sm font-mono font-bold tabular-nums text-zinc-800">{totals.hours.toFixed(2)}</div>
                  </div>
                  <div className="flex-1 min-w-[140px] basis-0 px-4 py-2" data-testid="clients-total-cost">
                    <div className="text-[9px] font-semibold uppercase tracking-wider text-amber-600">{t('totalCleanerCost')}</div>
                    <div className="text-sm font-mono font-bold tabular-nums text-amber-800">&euro;{totals.cost.toFixed(2)}</div>
                  </div>
                </div>
              );
            })()}
            {/* Phase 471 — Accordion view (legacy). Default on small screens. */}
            {clientsViewMode === 'accordion' && (
            <div className="space-y-3">
              {filteredClients.map(c => {
                const Icon = getClientIcon(c.client_type);
                return (
                <div key={c.client_id} className={`bg-white border ${selectedClientIds.has(c.client_id) ? 'border-zinc-900' : 'border-zinc-200'}`} data-testid={`client-card-${c.client_id}`}>
                  <div className="flex items-center justify-between px-5 py-4">
                    <div className="flex items-center gap-4 flex-1 min-w-0">
                      <input
                        type="checkbox"
                        className="accent-zinc-900 w-4 h-4 cursor-pointer shrink-0"
                        checked={selectedClientIds.has(c.client_id)}
                        onChange={() => toggleSelectedClient(c.client_id)}
                        data-testid={`client-select-${c.client_id}`}
                      />
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${getClientColor(c.client_type).split(' ')[0]}`}><Icon size={24} className="text-zinc-400" /></div>
                      <div className="min-w-0">
                        <h3 className="font-semibold text-zinc-900 truncate">{c.name}</h3>
                        <div className="flex items-center gap-3 text-xs text-zinc-500 mt-0.5">
                          {c.phone && <span className="flex items-center gap-1"><Phone size={12} /> {c.phone}</span>}
                          <span className={`px-1.5 py-0.5 font-medium border ${getClientColor(c.client_type)}`}>{getClientTypeLabel(c.client_type)}</span>
                          {c.source === 'building_auto' && <span className="px-1.5 py-0.5 font-medium border border-teal-200 bg-teal-50 text-teal-700" data-testid={`auto-badge-${c.client_id}`}>{t('autoFromBuilding')}</span>}
                          {c.date && <span className="text-zinc-400">{fmtDate(c.date)}</span>}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right mr-2">
                        <div className="flex gap-4 text-xs">
                          <div><span className="text-zinc-400 block">{t('totalJobs')}</span><span className="font-mono font-semibold">{c.total_jobs}</span></div>
                          <div><span className="text-zinc-400 block">{t('totalHours')}</span><span className="font-mono font-semibold">{c.total_hours?.toFixed(1)}</span></div>
                          <div><span className="text-zinc-400 block">{t('cleanerCost')}</span><span className="font-mono font-semibold text-amber-700">&euro;{c.total_cost?.toFixed(2)}</span></div>
                        </div>
                      </div>
                      {/* Phase 100 — Payments are handled exclusively from the Finance module. This button deep-links there instead. */}
                      <Button variant="ghost" size="sm" onClick={() => openClientDialog(c)}><PencilSimple size={16} /></Button>
                      <Button variant="ghost" size="sm" className="text-red-500" onClick={() => setDeletingClient(c)}><Trash size={16} /></Button>
                      <button onClick={() => toggleClientExpand(c)} className="text-zinc-400 hover:text-zinc-700">{expandedClient === c.client_id ? <CaretUp size={16} /> : <CaretDown size={16} />}</button>
                    </div>
                  </div>
                  {expandedClient === c.client_id && (
                    <div className="border-t border-zinc-100 bg-zinc-50/50" data-testid={`client-history-${c.client_id}`}>
                      <ClientJobsPanel
                        loading={isClientDetailLoading}
                        jobs={(clientHistory && clientHistory.client && clientHistory.client.client_id === c.client_id) ? (clientHistory.jobs || []) : []}
                        testIdPrefix={`client-jobs-${c.client_id}`}
                      />
                    </div>
                  )}
                </div>
              );})}
              {filteredClients.length === 0 && <p className="text-center text-zinc-400 py-12 text-sm">{t('noData')}</p>}
            </div>
            )}

            {/* Phase 471 — Split master/detail view (default on ≥1024px). */}
            {clientsViewMode === 'split' && (() => {
              const selectedC = filteredClients.find(c => c.client_id === expandedClient) || null;
              const allMyJobs = (selectedC && clientHistory && clientHistory.client && clientHistory.client.client_id === selectedC.client_id) ? (clientHistory.jobs || []) : [];
              return (
              <div className="grid grid-cols-[minmax(280px,340px)_1fr] gap-4" data-testid="clients-split-view">
                {/* LEFT — client list */}
                <div className="bg-white border border-zinc-200 max-h-[calc(100vh-260px)] overflow-y-auto" data-testid="clients-split-left">
                  {filteredClients.length === 0 ? (
                    <div className="px-4 py-8 text-center text-xs text-zinc-400">{t('noClientsFound') || t('noData') || 'No clients found'}</div>
                  ) : filteredClients.map(c => {
                    const Icon = getClientIcon(c.client_type);
                    const isSel = expandedClient === c.client_id;
                    return (
                      <button
                        key={c.client_id}
                        onClick={() => selectClient(c)}
                        className={`w-full text-left flex items-center gap-3 px-4 py-2.5 border-b border-zinc-100 transition-colors ${
                          isSel
                            ? 'bg-zinc-900 text-white border-zinc-900'
                            : 'bg-white hover:bg-zinc-50 text-zinc-800'
                        }`}
                        data-testid={`split-client-${c.client_id}`}
                        aria-current={isSel ? 'true' : 'false'}
                      >
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${isSel ? 'bg-zinc-700' : (getClientColor(c.client_type).split(' ')[0])}`}>
                          <Icon size={18} className={isSel ? 'text-white' : 'text-zinc-500'} />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className={`text-sm font-semibold truncate ${isSel ? 'text-white' : 'text-zinc-900'}`}>{c.name}</div>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className={`text-[10px] font-medium px-1.5 py-0.5 border ${isSel ? 'border-white/30 text-white/80' : getClientColor(c.client_type)}`}>{getClientTypeLabel(c.client_type)}</span>
                            <span className={`text-[10px] font-mono ${isSel ? 'text-white/70' : 'text-zinc-500'}`}>{c.total_jobs || 0} {t('jobs') || 'jobs'}</span>
                          </div>
                        </div>
                        {/* Phase 622 — Sidebar pill = canonical customer
                            balance. SINGLE SOURCE OF TRUTH per user
                            directive: the same number rendered in the
                            Outstanding KPI, the Statement final-row
                            balance and Finance → Customer balance.
                            Backend field `customer_balance` already
                            computes `Σ invoiced − Σ all customer
                            payments (incl. unallocated bank receipts)`
                            in the /cleaning/clients endpoint. We do
                            NOT recompute here — that's how drift
                            sneaks back in. */}
                      </button>
                    );
                  })}
                </div>

                {/* RIGHT — detail panel */}
                <div className="bg-white border border-zinc-200 min-h-[400px]" data-testid="clients-split-right">
                  {!selectedC ? (
                    <div className="flex items-center justify-center min-h-[400px] text-center px-6 py-12" data-testid="split-no-selection">
                      <div>
                        <UserCircle size={48} className="mx-auto text-zinc-300 mb-3" />
                        <p className="text-sm text-zinc-500">{t('selectAClient') || 'Select a client from the list'}</p>
                        <p className="text-xs text-zinc-400 mt-1">{t('selectAClientJobsHint') || 'Επιλέξτε πελάτη για να δείτε τις εργασίες καθαρισμού.'}</p>
                      </div>
                    </div>
                  ) : isClientDetailLoading ? (
                    <div className="p-6 space-y-3" data-testid="split-loading">
                      <div className="h-6 bg-zinc-100 animate-pulse w-1/3"></div>
                      <div className="grid grid-cols-5 gap-2">
                        {[0,1,2,3,4].map(i => <div key={i} className="h-16 bg-zinc-100 animate-pulse"></div>)}
                      </div>
                      <div className="h-8 bg-zinc-100 animate-pulse w-2/3"></div>
                      <div className="h-64 bg-zinc-100 animate-pulse"></div>
                    </div>
                  ) : (
                    <div>
                      {/* Sticky action bar */}
                      <div className="sticky top-0 z-10 flex items-center justify-between gap-3 px-5 py-3 bg-white border-b border-zinc-200" data-testid="split-action-bar">
                        <div className="flex items-center gap-3 min-w-0">
                          {(() => { const Icon = getClientIcon(selectedC.client_type); return <div className={`w-10 h-10 rounded-full flex items-center justify-center ${getClientColor(selectedC.client_type).split(' ')[0]}`}><Icon size={22} className="text-zinc-400" /></div>; })()}
                          <div className="min-w-0">
                            <h3 className="font-bold text-zinc-900 truncate text-base">{selectedC.name}</h3>
                            <div className="flex items-center gap-2 text-xs text-zinc-500 mt-0.5">
                              {selectedC.phone && <span className="flex items-center gap-1"><Phone size={11} /> {selectedC.phone}</span>}
                              <span className={`px-1.5 py-0.5 font-medium border ${getClientColor(selectedC.client_type)}`}>{getClientTypeLabel(selectedC.client_type)}</span>
                              {selectedC.source === 'building_auto' && <span className="px-1.5 py-0.5 font-medium border border-teal-200 bg-teal-50 text-teal-700">{t('autoFromBuilding')}</span>}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          {/* Phase 479 — Money creation (Invoice / Payment) is now
                              owned exclusively by the Finance module. Only the
                              operational action (Add Job) remains here; Finance
                              is one click away via "View in Finance". */}
                          <Button size="sm" variant="outline" className="rounded-none h-8 text-xs" onClick={() => { setJobForm({ date: new Date().toISOString().split('T')[0], client_id: selectedC.client_id, notes: '' }); setJobCleanerRows([{ cleaner_id: '', hours_worked: '', cleaner_earned: '' }]); setEditingJob(null); setJobDialog(true); }} data-testid={`split-add-job-${selectedC.client_id}`}>
                            <Plus size={13} className="mr-1" /> {t('addJob') || 'Add Job'}
                          </Button>
                          <Button variant="ghost" size="sm" className="h-8" onClick={() => openClientDialog(selectedC)} title={t('edit')} data-testid={`split-edit-client-${selectedC.client_id}`}><PencilSimple size={15} /></Button>
                          <Button variant="ghost" size="sm" className="h-8 text-red-500" onClick={() => setDeletingClient(selectedC)} title={t('delete')} data-testid={`split-delete-client-${selectedC.client_id}`}><Trash size={15} /></Button>
                        </div>
                      </div>

                      <ClientJobsPanel jobs={allMyJobs} testIdPrefix={`split-client-jobs-${selectedC.client_id}`} />
                    </div>
                  )}
                </div>
              </div>
              );
            })()}
          </div>
        )}

        {/* ============ JOB HISTORY TAB ============ */}
        {activeTab === 'jobHistory' && (
          <div data-testid="job-history-tab">
            <div className="flex flex-wrap justify-between items-center mb-4 gap-3">
              <div className="flex items-center gap-3 flex-wrap">
                <p className="text-sm text-zinc-500">{filteredJobs.length} {t('totalJobs').toLowerCase()}</p>
                <div className="relative"><MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400" /><Input value={jobSearch} onChange={e => setJobSearch(e.target.value)} placeholder={t('search')} className="rounded-none pl-8 h-8 w-48 text-xs" data-testid="job-search" /></div>
                <Select value={jobCleanerFilter} onValueChange={setJobCleanerFilter}><SelectTrigger className="rounded-none h-8 w-44 text-xs" data-testid="job-cleaner-filter"><FunnelSimple size={14} className="mr-1" /><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">{t('all')} {t('cleaners')}</SelectItem>{cleaners.map(c => <SelectItem key={c.cleaner_id} value={c.cleaner_id}>{c.name}</SelectItem>)}</SelectContent></Select>
                <Select value={jobClientFilter} onValueChange={setJobClientFilter}><SelectTrigger className="rounded-none h-8 w-44 text-xs" data-testid="job-client-filter"><FunnelSimple size={14} className="mr-1" /><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">{t('all')} {t('clients')}</SelectItem>{clients.map(c => <SelectItem key={c.client_id} value={c.client_id}>{c.name}</SelectItem>)}</SelectContent></Select>
                {/* Phase 365 — Unified date filter (Date / Month / Range / All). */}
                <DateModeSelector
                  mode={jobDateMode}
                  setMode={setJobDateMode}
                  from={jobDateFrom}
                  to={jobDateTo}
                  onChange={({ from, to }) => { setJobDateFrom(from); setJobDateTo(to); }}
                  allowAll
                  showLabels={false}
                  testIdPrefix="job-datemode"
                  size="sm"
                />
                <SortToggle testId="job-sort-toggle" />
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" className="rounded-none" onClick={exportJobsPdf} data-testid="export-jobs-pdf"><FilePdf size={16} className="mr-1" /> {t('exportPdf')}</Button>
                <Button onClick={() => openJobDialog()} size="sm" className="rounded-none" data-testid="add-job-btn"><Plus size={16} className="mr-1" /> {t('addJob')}</Button>
              </div>
            </div>
            {/* Phase 263 — Bulk action bar (jobs). */}
            {selectedJobIds.size > 0 && (
              <div className="flex items-center justify-between mb-3 px-3 py-2 bg-zinc-900 text-white" data-testid="jobs-bulk-bar">
                <div className="text-xs">
                  <strong>{selectedJobIds.size}</strong> {t('selected') || 'selected'}
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => clearSelection('job')} className="text-xs text-zinc-300 hover:text-white px-2 py-1" data-testid="jobs-clear-selection">{t('clear') || 'Clear'}</button>
                  <Button size="sm" variant="destructive" className="rounded-none h-7 text-xs" onClick={() => setBulkDeleteConfirm({ kind: 'job', ids: [...selectedJobIds] })} data-testid="jobs-bulk-delete-btn">
                    <Trash size={14} className="mr-1" /> {t('deleteSelected') || 'Delete Selected'}
                  </Button>
                </div>
              </div>
            )}
            {filteredJobs.length > 0 && (
              <div className="bg-zinc-50 border border-zinc-200 p-4 mb-4 flex flex-wrap gap-6" data-testid="jobs-summary-bar">
                <div><span className="text-xs text-zinc-400 block">{t('totalJobs')}</span><span className="font-mono font-bold">{jobTotals.totalJobs}</span></div>
                <div><span className="text-xs text-zinc-400 block">{t('totalHours')}</span><span className="font-mono font-bold">{jobTotals.totalHours.toFixed(1)}</span></div>
                <div><span className="text-xs text-zinc-400 block">{t('cleanerCost')}</span><span className="font-mono font-bold text-amber-700">&euro;{jobTotals.totalCleanerCost.toFixed(2)}</span></div>
              </div>
            )}
            <div className="bg-white border border-zinc-200 overflow-x-auto">
              <table className="w-full text-sm" data-testid="jobs-table">
                <thead><tr className="bg-zinc-50 border-b border-zinc-200 text-xs text-zinc-500 uppercase">
                  <th className="w-10 px-3 py-2.5">
                    <input
                      type="checkbox"
                      className="accent-zinc-900"
                      checked={filteredJobs.length > 0 && selectedJobIds.size === filteredJobs.length}
                      onChange={(e) => e.target.checked ? setAllSelected('job', filteredJobs) : clearSelection('job')}
                      data-testid="jobs-select-all"
                    />
                  </th>
                  <th className="text-left px-4 py-2.5 font-medium">{t('date')}</th>
                  <th className="text-left px-4 py-2.5 font-medium">{t('clients')}</th>
                  <th className="text-left px-4 py-2.5 font-medium">{t('cleaners')}</th>
                  <th className="text-right px-4 py-2.5 font-medium">{t('hoursWorked')}</th>
                  <th className="text-right px-4 py-2.5 font-medium">{t('cleanerCost')}</th>
                  <th className="text-left px-4 py-2.5 font-medium">{t('notes')}</th>
                  <th className="w-20 px-4 py-2.5"></th>
                </tr></thead>
                <tbody>
                  {filteredJobs.map(j => {
                    const jid = j._flat_id || j.job_id;
                    return (
                    <tr key={jid} className={`border-b border-zinc-100 hover:bg-zinc-50/50 ${selectedJobIds.has(jid) ? 'bg-zinc-100' : ''}`} data-testid={`job-row-${jid}`}>
                      <td className="px-3 py-2.5"><input type="checkbox" className="accent-zinc-900" checked={selectedJobIds.has(jid)} onChange={() => toggleSelectedJob(jid)} data-testid={`job-select-${jid}`} /></td>
                      <td className="px-4 py-2.5 font-mono text-xs">{fmtDate(j.date)}</td>
                      <td className="px-4 py-2.5 font-medium">{j.client_name}</td>
                      <td className="px-4 py-2.5 text-sm">{j.cleaner_names}</td>
                      <td className="px-4 py-2.5 text-right font-mono">{j.total_hours?.toFixed(1)}</td>
                      <td className="px-4 py-2.5 text-right font-mono text-amber-700">&euro;{j.total_cleaner_cost?.toFixed(2)}</td>
                      <td className="px-4 py-2.5 text-zinc-500 max-w-[150px] truncate">{j.notes || '-'}</td>
                      <td className="px-4 py-2.5"><div className="flex gap-1"><button onClick={() => openJobDialog(j)} className="text-zinc-400 hover:text-zinc-700"><PencilSimple size={15} /></button><button onClick={() => setDeletingJob(j)} className="text-zinc-400 hover:text-red-600"><Trash size={15} /></button></div></td>
                    </tr>
                    );
                  })}
                  {filteredJobs.length === 0 && <tr><td colSpan={8} className="text-center py-12 text-zinc-400 text-sm">{t('noData')}</td></tr>}
                </tbody>
                {filteredJobs.length > 0 && (
                  <tfoot><tr className="border-t-2 border-zinc-300 bg-zinc-50 font-semibold text-xs">
                    <td></td>
                    <td colSpan={3} className="px-4 py-2 text-right uppercase text-zinc-400">{t('total')}</td>
                    <td className="px-4 py-2 text-right font-mono">{jobTotals.totalHours.toFixed(1)}</td>
                    <td className="px-4 py-2 text-right font-mono text-amber-700">&euro;{jobTotals.totalCleanerCost.toFixed(2)}</td>
                    <td colSpan={2}></td>
                  </tr></tfoot>
                )}
              </table>
            </div>
          </div>
        )}

        {/* ============ CLEANING HISTORY TAB ============ */}
        {activeTab === 'cleaningSchedule' && (
          <div data-testid="cleaning-schedule-tab"><CleaningSchedule /></div>
        )}

        {activeTab === 'cleaningHistory' && (
          <div data-testid="cleaning-history-tab">
            <CleaningHistory embedded presetCleaner={addBuildingCleaningPreset} openAddSignal={addBuildingCleaningSignal} />
          </div>
        )}



        {/* Phase 477 — Categories management Dialog (moved from a top-level tab). */}
        <Dialog open={categoriesDialog} onOpenChange={setCategoriesDialog}>
          <DialogContent className="sm:max-w-lg" data-testid="categories-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Tag size={18} className="text-zinc-500" />
                {t('manageCategories') || 'Manage Categories'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3" data-testid="categories-tab">
              <div className="flex justify-between items-center">
                <p className="text-xs text-zinc-500">{categories.length} {t('categories')}</p>
                <Button onClick={() => openCatDialog()} size="sm" className="rounded-none h-7 text-xs" data-testid="add-category-btn">
                  <Plus size={13} className="mr-1" /> {t('add')}
                </Button>
              </div>
              <div className="space-y-1.5 max-h-[420px] overflow-y-auto">
                {categories.map(c => (
                  <div key={c.category_id} className="bg-white border border-zinc-200 px-3 py-2 flex items-center justify-between" data-testid={`cat-${c.category_id}`}>
                    <div className="flex items-center gap-3">
                      <Tag size={16} className="text-zinc-400" />
                      <div>
                        <span className="font-medium text-zinc-900 text-sm">{c.name}</span>
                        {c.name_el && <span className="text-xs text-zinc-400 ml-2">({c.name_el})</span>}
                        {c.is_default && <span className="ml-2 text-[10px] px-1.5 py-0.5 bg-zinc-100 text-zinc-500 border border-zinc-200 font-medium">DEFAULT</span>}
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => openCatDialog(c)} data-testid={`edit-cat-${c.category_id}`}><PencilSimple size={14} /></Button>
                      {!c.is_default && <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500" onClick={() => setDeletingCat(c)}><Trash size={14} /></Button>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCategoriesDialog(false)} className="rounded-none">{t('close') || 'Close'}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ============ DIALOGS ============ */}

        {/* Cleaner Dialog */}
        <Dialog open={cleanerDialog} onOpenChange={setCleanerDialog}>
          <DialogContent className="sm:max-w-md" data-testid="cleaner-dialog">
            <DialogHeader><DialogTitle>{editingCleaner ? t('editCleaner') : t('addCleaner')}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>{t('name')} *</Label><Input value={cleanerForm.name} onChange={e => setCleanerForm(f => ({ ...f, name: e.target.value }))} className="rounded-none" data-testid="cleaner-name-input" /></div>
              <div><Label>{t('phone')}</Label><Input value={cleanerForm.phone} onChange={e => setCleanerForm(f => ({ ...f, phone: e.target.value }))} className="rounded-none" /></div>
              <div><Label>{t('paymentType')}</Label><Select value={cleanerForm.payment_type} onValueChange={v => setCleanerForm(f => ({ ...f, payment_type: v }))}><SelectTrigger className="rounded-none" data-testid="cleaner-payment-type"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="hourly">{t('perHour')}</SelectItem><SelectItem value="monthly">{t('perMonth')}</SelectItem></SelectContent></Select></div>
              {cleanerForm.payment_type === 'hourly' ? <div><Label>{t('hourlyRate')} (&euro;)</Label><Input type="number" step="0.01" value={cleanerForm.hourly_rate} onChange={e => setCleanerForm(f => ({ ...f, hourly_rate: e.target.value }))} className="rounded-none" data-testid="cleaner-hourly-rate" /></div>
                : <div><Label>{t('monthlyRate')} (&euro;)</Label><Input type="number" step="0.01" value={cleanerForm.monthly_rate} onChange={e => setCleanerForm(f => ({ ...f, monthly_rate: e.target.value }))} className="rounded-none" /></div>}
              <div><Label>{t('notes')}</Label><Input value={cleanerForm.notes} onChange={e => setCleanerForm(f => ({ ...f, notes: e.target.value }))} className="rounded-none" /></div>
            </div>
            <DialogFooter><Button variant="outline" onClick={() => setCleanerDialog(false)} className="rounded-none">{t('cancel')}</Button><Button onClick={saveCleaner} className="rounded-none" data-testid="save-cleaner-btn">{t('save')}</Button></DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Cleaner Payment Dialog — Phase 712.1: simplified. No more
            per-charge allocations or outstanding-debits table. Each
            payment just carries Amount + Payment Date + Payroll Month
            (required) + Notes. */}
        <Dialog open={paymentDialog} onOpenChange={(o) => { if (!o) { setPaymentEditingId(null); } setPaymentDialog(o); }}>
          <DialogContent className="sm:max-w-md p-5" data-testid="cleaner-payment-dialog">
            <DialogHeader><DialogTitle>{paymentEditingId ? (t('editPayment') || 'Edit Payment') : (t('recordPayment') || 'Record Payment')}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>{t('amount')} (&euro;) *</Label>
                <Input type="number" step="0.01" value={paymentForm.amount} onChange={e => setPaymentForm(f => ({ ...f, amount: e.target.value }))} className="rounded-none" data-testid="payment-amount-input" />
              </div>
              <div>
                <Label>{t('paymentDate') || 'Payment Date'} *</Label>
                <DatePickerDMY value={paymentForm.date} onChange={v => setPaymentForm(f => ({...f, date: v}))} className="rounded-none" data-testid="payment-date-input" />
                <p className="text-[10px] text-zinc-400 mt-1">{t('paymentDateHelp') || 'The actual date the cleaner was paid.'}</p>
              </div>
              {/* Phase 712.1 — Payroll Month picker (required).
                  Determines which month the payment counts towards on the
                  Monthly Payroll screen, independent of the payment date. */}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label>{t('payrollMonth') || 'Payroll Month'} *</Label>
                  <Select value={(paymentForm.payroll_month || '').slice(5, 7)} onValueChange={(mm) => {
                    const yy = (paymentForm.payroll_month || '').slice(0, 4) || String(new Date().getFullYear());
                    setPaymentForm(f => ({ ...f, payroll_month: `${yy}-${mm}` }));
                  }}>
                    <SelectTrigger className="rounded-none" data-testid="payment-payroll-month-select"><SelectValue placeholder={t('selectMonth') || 'Month'} /></SelectTrigger>
                    <SelectContent>
                      {(language === 'el'
                        ? ['Ιανουάριος','Φεβρουάριος','Μάρτιος','Απρίλιος','Μάιος','Ιούνιος','Ιούλιος','Αύγουστος','Σεπτέμβριος','Οκτώβριος','Νοέμβριος','Δεκέμβριος']
                        : ['January','February','March','April','May','June','July','August','September','October','November','December']
                      ).map((nm, i) => (
                        <SelectItem key={i} value={String(i + 1).padStart(2, '0')}>{nm}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>{t('year') || 'Year'} *</Label>
                  <Select value={(paymentForm.payroll_month || '').slice(0, 4)} onValueChange={(yy) => {
                    const mm = (paymentForm.payroll_month || '').slice(5, 7) || String(new Date().getMonth() + 1).padStart(2, '0');
                    setPaymentForm(f => ({ ...f, payroll_month: `${yy}-${mm}` }));
                  }}>
                    <SelectTrigger className="rounded-none" data-testid="payment-payroll-year-select"><SelectValue placeholder={t('selectYear') || 'Year'} /></SelectTrigger>
                    <SelectContent>
                      {(() => {
                        const yy = new Date().getFullYear();
                        const opts = [];
                        for (let y = yy + 1; y >= yy - 5; y--) opts.push(String(y));
                        return opts.map(y => <SelectItem key={y} value={y}>{y}</SelectItem>);
                      })()}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <p className="text-[10px] text-zinc-400 -mt-1">{t('payrollMonthHelp') || 'Indicates which month\'s work this payment belongs to. A payment made in June can be for May\'s work.'}</p>
              <div>
                <Label>{t('notes')}</Label>
                <Input value={paymentForm.notes} onChange={e => setPaymentForm(f => ({ ...f, notes: e.target.value }))} className="rounded-none" data-testid="payment-notes-input" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setPaymentDialog(false)} className="rounded-none">{t('cancel')}</Button>
              <Button onClick={savePayment} className="rounded-none" data-testid="save-payment-btn">{t('save')}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>


        {/* Client Dialog */}
        <Dialog open={clientDialog} onOpenChange={setClientDialog}>
          <DialogContent className="sm:max-w-md" data-testid="client-dialog">
            <DialogHeader><DialogTitle>{editingClient ? t('editClient') : t('addClient')}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>{t('clientType')}</Label>
                <Select value={clientForm.client_type} onValueChange={v => setClientForm(f => ({ ...f, client_type: v, building_id: '' }))}>
                  <SelectTrigger className="rounded-none" data-testid="client-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {categories.map(c => {
                      const IconC = c.name === 'House' ? House : c.name === 'Building' ? Buildings : c.name === 'Office' ? OfficeChair : Broom;
                      return <SelectItem key={c.category_id} value={c.name.toLowerCase().replace(/\s+/g, '_')}><div className="flex items-center gap-2"><IconC size={14} /> {language === 'el' ? (c.name_el || c.name) : c.name}</div></SelectItem>;
                    })}
                  </SelectContent>
                </Select>
              </div>
              {clientForm.client_type === 'building' && (
                <div><Label>{t('selectBuilding')}</Label><Select value={clientForm.building_id} onValueChange={v => { const b = buildingsList.find(bb => bb.building_id === v); setClientForm(f => ({ ...f, building_id: v, name: b?.name || f.name })); }}><SelectTrigger className="rounded-none" data-testid="building-select"><SelectValue placeholder={t('selectBuilding')} /></SelectTrigger><SelectContent>{buildingsList.map(b => <SelectItem key={b.building_id} value={b.building_id}>{b.name}</SelectItem>)}</SelectContent></Select></div>
              )}
              <div><Label>{t('name')} *</Label><Input value={clientForm.name} onChange={e => setClientForm(f => ({ ...f, name: e.target.value }))} className="rounded-none" data-testid="client-name-input" /></div>
              <div><Label>{t('phone')}</Label><Input value={clientForm.phone} onChange={e => setClientForm(f => ({ ...f, phone: e.target.value }))} className="rounded-none" /></div>
              <div><Label>{t('address')}</Label><Input value={clientForm.address} onChange={e => setClientForm(f => ({ ...f, address: e.target.value }))} className="rounded-none" data-testid="client-address-input" /></div>
              <div><Label>{t('notes')}</Label><Input value={clientForm.notes} onChange={e => setClientForm(f => ({ ...f, notes: e.target.value }))} className="rounded-none" /></div>
            </div>
            <DialogFooter><Button variant="outline" onClick={() => setClientDialog(false)} className="rounded-none">{t('cancel')}</Button><Button onClick={saveClient} className="rounded-none" data-testid="save-client-btn">{t('save')}</Button></DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Client Payment Dialog */}
        <Dialog open={clientPayDialog} onOpenChange={setClientPayDialog}>
          <DialogContent className="sm:max-w-md" data-testid="client-payment-dialog">
            <DialogHeader><DialogTitle>{t('recordPayment')}: {clientPayTarget?.name}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>{t('amount')} (&euro;) *</Label><Input type="number" step="0.01" value={clientPayForm.amount} onChange={e => setClientPayForm(f => ({ ...f, amount: e.target.value }))} className="rounded-none" data-testid="client-pay-amount" /></div>
              <div><Label>{t('date')}</Label><DatePickerDMY value={clientPayForm.date} onChange={v => setClientPayForm(f => ({...f, date: v}))} className="rounded-none" data-testid="client-pay-date" /></div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label>{t('period')} ({t('month')})</Label><Select value={clientPayForm.period_month} onValueChange={v => setClientPayForm(f => ({ ...f, period_month: v }))}><SelectTrigger className="rounded-none" data-testid="client-pay-month"><SelectValue /></SelectTrigger><SelectContent>{Array.from({ length: 12 }, (_, i) => <SelectItem key={i} value={String(i + 1)}>{new Date(2000, i).toLocaleString('en', { month: 'long' })}</SelectItem>)}</SelectContent></Select></div>
                <div><Label>{t('year')}</Label><Input type="number" value={clientPayForm.period_year} onChange={e => setClientPayForm(f => ({ ...f, period_year: e.target.value }))} className="rounded-none" data-testid="client-pay-year" /></div>
              </div>
              <div><Label>{t('notes')}</Label><Input value={clientPayForm.notes} onChange={e => setClientPayForm(f => ({ ...f, notes: e.target.value }))} className="rounded-none" /></div>
            </div>
            <DialogFooter><Button variant="outline" onClick={() => setClientPayDialog(false)} className="rounded-none">{t('cancel')}</Button><Button onClick={saveClientPayment} className="rounded-none" data-testid="save-client-pay-btn">{t('save')}</Button></DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Job Dialog (Multi-cleaner) */}
        <Dialog open={jobDialog} onOpenChange={setJobDialog}>
          <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="job-dialog">
            <DialogHeader><DialogTitle>{editingJob ? t('editJob') : t('addJob')}</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>{t('selectClient')} *</Label>
                <Select value={jobForm.client_id} onValueChange={v => setJobForm(f => ({ ...f, client_id: v }))}>
                  <SelectTrigger className="rounded-none" data-testid="job-client-select"><SelectValue placeholder={t('selectClient')} /></SelectTrigger>
                  <SelectContent>
                    {/* Phase 365 — Only cleaning-management Clients are shown.
                        Buildings from Building Management are NOT listed here;
                        the data source `clients` is from /cleaning/clients. */}
                    {['house', 'office', 'building', 'general'].map(type => {
                      const group = clients.filter(c => c.client_type === type);
                      if (!group.length) return null;
                      const label = type === 'house' ? t('clients') : type === 'office' ? t('offices') : type === 'building' ? t('buildings') : t('generalCleaning');
                      const IconC = getClientIcon(type);
                      return (<React.Fragment key={type}>
                        <div className="px-2 py-1 text-[10px] font-semibold text-zinc-400 uppercase">{label}</div>
                        {group.map(c => <SelectItem key={c.client_id} value={c.client_id}><span className="flex items-center gap-1.5"><IconC size={12} /> {c.name}</span></SelectItem>)}
                      </React.Fragment>);
                    })}
                    {clients.filter(c => !['house', 'office', 'building', 'general'].includes(c.client_type)).length > 0 && <>
                      <div className="px-2 py-1 text-[10px] font-semibold text-zinc-400 uppercase">{t('other')}</div>
                      {clients.filter(c => !['house', 'office', 'building', 'general'].includes(c.client_type)).map(c => <SelectItem key={c.client_id} value={c.client_id}>{c.name}</SelectItem>)}
                    </>}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>{t('date')} *</Label><DatePickerDMY value={jobForm.date} onChange={v => setJobForm(f => ({...f, date: v}))} className="rounded-none" data-testid="job-date-input" /></div>

              {/* Cleaners section */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-sm font-semibold">{t('cleaners')} ({jobCleanerRows.length})</Label>
                  {jobCleanerRows.length < 10 && <Button type="button" variant="outline" size="sm" className="rounded-none text-xs" onClick={addCleanerRow} data-testid="add-cleaner-row"><Plus size={14} className="mr-1" /> {t('addCleaner')}</Button>}
                </div>
                <div className="space-y-2">
                  {/* Phase 712.2 — Merge any cleaner that's currently
                      assigned but missing from the active-cleaners
                      list (e.g. the cleaner was marked inactive after
                      the job was created). Without this fallback the
                      Select would render blank for those rows even
                      though the value is set. The synthetic option is
                      marked "(inactive)" so the user understands. */}
                  {(() => {
                    const known = new Set(cleaners.map(c => c.cleaner_id));
                    const missing = [];
                    for (const r of jobCleanerRows) {
                      if (!r.cleaner_id || known.has(r.cleaner_id)) continue;
                      // Pull the name from the editingJob.cleaners array
                      // (preserved during preload).
                      const fromJob = (editingJob?.cleaners || []).find(cl => cl.cleaner_id === r.cleaner_id);
                      missing.push({
                        cleaner_id: r.cleaner_id,
                        name: (fromJob?.cleaner_name || r.cleaner_id) + ' ' + (t('inactiveSuffix') || '(inactive)'),
                      });
                      known.add(r.cleaner_id);
                    }
                    // Stash on the closure for the Select to pick up.
                    window.__missingCleanersForJob = missing;
                    return null;
                  })()}
                  {jobCleanerRows.map((row, idx) => (
                    <div key={idx} className="flex items-end gap-2 p-3 border border-zinc-200 bg-zinc-50" data-testid={`cleaner-row-${idx}`}>
                      <div className="flex-1 min-w-[140px]">
                        <Label className="text-xs">{t('selectCleaner')}</Label>
                        <Select value={row.cleaner_id} onValueChange={v => updateCleanerRow(idx, 'cleaner_id', v)}>
                          <SelectTrigger className="rounded-none h-9 text-xs" data-testid={`job-cleaner-select-${idx}`}><SelectValue placeholder={t('selectCleaner')} /></SelectTrigger>
                          <SelectContent>
                            {cleaners.map(c => <SelectItem key={c.cleaner_id} value={c.cleaner_id}>{c.name}</SelectItem>)}
                            {(window.__missingCleanersForJob || []).map(c => <SelectItem key={c.cleaner_id} value={c.cleaner_id} className="italic text-zinc-500">{c.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="w-20">
                        <Label className="text-xs">{t('hoursWorked')}</Label>
                        <Input
                          type="text"
                          inputMode="decimal"
                          value={row.hours_worked}
                          onChange={e => updateCleanerRow(idx, 'hours_worked', e.target.value)}
                          className="rounded-none h-9 text-xs"
                          placeholder="4.75 or 4:45"
                          data-testid={`job-hours-${idx}`}
                        />
                      </div>
                      <div className="w-24">
                        <Label className="text-xs">{t('cleanerEarned')} (&euro;)</Label>
                        <Input type="number" step="0.01" value={row.cleaner_earned} onChange={e => updateCleanerRow(idx, 'cleaner_earned', e.target.value)} className="rounded-none h-9 text-xs" data-testid={`job-earned-${idx}`} />
                      </div>
                      {jobCleanerRows.length > 1 && <button onClick={() => removeCleanerRow(idx)} className="text-red-400 hover:text-red-600 pb-1"><Trash size={16} /></button>}
                    </div>
                  ))}
                </div>
                <div className="mt-2 bg-zinc-100 border border-zinc-200 px-3 py-2 flex items-center justify-between">
                  <span className="text-xs font-medium text-zinc-500">{t('totalCleanerCost')}</span>
                  <span className="font-mono font-bold text-amber-700">&euro;{jobTotalCleanerCost.toFixed(2)}</span>
                </div>
              </div>

              <div><Label>{t('notes')}</Label><Input value={jobForm.notes} onChange={e => setJobForm(f => ({ ...f, notes: e.target.value }))} className="rounded-none" data-testid="job-notes-input" /></div>
            </div>
            <DialogFooter><Button variant="outline" onClick={() => setJobDialog(false)} className="rounded-none">{t('cancel')}</Button><Button onClick={saveJob} className="rounded-none" data-testid="save-job-btn">{t('save')}</Button></DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Statement Dialog */}
        <Dialog open={!!statementCleaner} onOpenChange={() => { setStatementCleaner(null); setStatementData(null); }}>
          <DialogContent
            className="!max-w-[1100px] !w-[92vw] p-0 overflow-hidden flex flex-col"
            style={{ resize: 'both', overflow: 'auto', minWidth: '720px', minHeight: '560px', maxWidth: '95vw', maxHeight: '92vh', height: '85vh' }}
            data-testid="statement-dialog"
          >
            <div className="px-6 pt-5 pb-3 border-b border-zinc-200 bg-white sticky top-0 z-10">
              <DialogHeader><DialogTitle className="text-base">{t('cleanerStatement')}: {statementCleaner?.name}</DialogTitle></DialogHeader>
              <div className="flex flex-wrap gap-3 items-end mt-3">
                <DateModeSelector
                  mode={stmtClnrDateMode}
                  setMode={setStmtClnrDateMode}
                  from={stmtClnrFrom}
                  to={stmtClnrTo}
                  onChange={({ from, to }) => { setStmtClnrFrom(from); setStmtClnrTo(to); }}
                  allowAll
                  testIdPrefix="stmt-clnr-datemode"
                  size="sm"
                />
                <div><Label className="text-xs">{t('client')}</Label><Select value={stmtClnrClientFilter} onValueChange={setStmtClnrClientFilter}><SelectTrigger className="rounded-none h-8 w-44 text-xs" data-testid="stmt-clnr-client-filter"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">{t('all')} {t('clients')}</SelectItem>{clients.map(c => <SelectItem key={c.client_id} value={c.client_id}>{c.name}</SelectItem>)}</SelectContent></Select></div>
                <SortToggle testId="stmt-clnr-sort-toggle" />
                <Button onClick={reloadCleanerStatement} size="sm" className="rounded-none h-8" data-testid="stmt-clnr-load">{t('search')}</Button>
              </div>
              {/* View Mode Tabs */}
              <div className="flex gap-0 mt-3 border border-zinc-200 w-fit" data-testid="stmt-view-mode-tabs">
                {[
                  { key: 'all', label: t('viewAllCombined') },
                  { key: 'buildings', label: t('viewBuildingsOnly') },
                  { key: 'jobs', label: t('viewJobsOnly') },
                ].map(tab => (
                  <button
                    key={tab.key}
                    onClick={() => setStmtViewMode(tab.key)}
                    className={`px-4 py-1.5 text-xs font-medium border-r border-zinc-200 last:border-r-0 transition-colors ${stmtViewMode === tab.key ? 'bg-zinc-900 text-white' : 'bg-white text-zinc-600 hover:bg-zinc-50'}`}
                    data-testid={`stmt-view-${tab.key}`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-4">
            {statementData && (
              <div className="space-y-5">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div className="bg-zinc-50 border border-zinc-200 p-3" data-testid="stmt-total-buildings"><span className="text-[10px] text-zinc-400 uppercase block">{t('buildingsCleaning')}</span><span className="text-lg font-bold font-mono">&euro;{statementData.total_buildings?.toFixed(2)}</span></div>
                  <div className="bg-zinc-50 border border-zinc-200 p-3" data-testid="stmt-total-jobs"><span className="text-[10px] text-zinc-400 uppercase block">{t('jobHistory')}</span><span className="text-lg font-bold font-mono">&euro;{statementData.total_jobs?.toFixed(2)}</span></div>
                  <div className="bg-zinc-50 border border-zinc-200 p-3" data-testid="stmt-total-earned"><span className="text-[10px] text-zinc-400 uppercase block">{t('totalEarned')}</span><span className="text-lg font-bold font-mono">&euro;{statementData.total_earned?.toFixed(2)}</span></div>
                  <div className="bg-zinc-50 border border-zinc-200 p-3" data-testid="stmt-total-paid"><span className="text-[10px] text-zinc-400 uppercase block">{t('paid')}</span><span className="text-lg font-bold font-mono text-emerald-700">&euro;{statementData.total_paid?.toFixed(2)}</span></div>
                </div>
                <div className={`border-l-4 px-3 py-2 ${statementData.remaining_balance > 0 ? 'border-red-500 bg-red-50' : 'border-emerald-500 bg-emerald-50'}`} data-testid="stmt-remaining"><span className="text-[10px] uppercase text-zinc-500 block">{t('remainingBalance')}</span><span className={`text-lg font-bold font-mono ${statementData.remaining_balance > 0 ? 'text-red-600' : 'text-emerald-700'}`}>&euro;{statementData.remaining_balance?.toFixed(2)}</span></div>

                {/* Combined View — FLAT timeline (Phase 475). Each entry
                    shows the actual client/building name as the primary
                    identifier. Generic "Building Cleaning / Job History"
                    labels are demoted to a secondary category badge. */}
                {stmtViewMode === 'all' && (
                  <div data-testid="stmt-combined-section">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-xs font-semibold text-zinc-500 uppercase">{t('viewAllCombined')}</h4>
                      <div className="flex items-center gap-2 text-xs">
                        <Label className="text-[10px] uppercase text-zinc-400">{t('clientOrBuilding') || 'Client / Building'}</Label>
                        <select
                          value={stmtNameFilter}
                          onChange={(e) => setStmtNameFilter(e.target.value)}
                          className="border border-zinc-200 bg-white px-2 py-1 text-xs focus:outline-none focus:border-zinc-400"
                          data-testid="stmt-name-filter"
                        >
                          <option value="all">{t('all') || 'All'} ({combinedStmtRows.length})</option>
                          {stmtNameOptions.map(n => <option key={n} value={n}>{n}</option>)}
                        </select>
                      </div>
                    </div>
                    <div className="border border-zinc-200">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="bg-zinc-50 border-b text-zinc-400">
                            <th className="text-left px-3 py-1.5 w-24">{t('date')}</th>
                            <th className="text-left px-3 py-1.5">{t('clientOrBuilding') || 'Client / Building'}</th>
                            <th className="text-left px-3 py-1.5 w-32">{t('category') || 'Category'}</th>
                            <th className="text-right px-3 py-1.5 w-20">{t('hoursShort') || t('totalHours') || 'Hours'}</th>
                            <th className="text-right px-3 py-1.5 w-24">{t('amount')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredCombinedStmtRows.length === 0 ? (
                            <tr><td colSpan={5} className="text-center py-6 text-zinc-400">{t('noData')}</td></tr>
                          ) : filteredCombinedStmtRows.map((row, i) => {
                            const accent =
                              row.categoryTone === 'amber'
                                ? 'bg-amber-50 text-amber-700 border-amber-200'
                                : 'bg-emerald-50 text-emerald-700 border-emerald-200';
                            const stripe = row.categoryTone === 'amber' ? 'bg-amber-400' : 'bg-emerald-400';
                            return (
                              <tr
                                key={`${row.kind}-${i}`}
                                className="border-b border-zinc-100 hover:bg-zinc-50/60 relative"
                                data-testid={`combined-row-${i}`}
                              >
                                <td className="px-3 py-1.5 font-mono relative">
                                  <span className={`absolute left-0 top-0 bottom-0 w-1 ${stripe}`} aria-hidden></span>
                                  {fmtDate(row.date)}
                                </td>
                                <td className="px-3 py-1.5 max-w-[420px]">
                                  <div className="font-semibold text-zinc-900 truncate" data-testid={`combined-name-${i}`} title={row.name || ''}>{row.name}</div>
                                  {row.description && (
                                    <div className="text-[10px] text-zinc-500 truncate" title={row.description}>{ledgerLabel(row.description, 80)}</div>
                                  )}
                                </td>
                                <td className="px-3 py-1.5">
                                  <span className={`inline-flex items-center text-[10px] font-medium px-1.5 py-0.5 border ${accent}`} data-testid={`combined-cat-${i}`}>
                                    {row.kind === 'building' ? (t('buildingsCleaning') || 'Building Cleaning') : (t('jobHistory') || 'Job')}
                                    {row.kind === 'job' && row.categoryLabel && row.categoryLabel !== 'Job' && (
                                      <span className="ml-1 text-zinc-500">· {row.categoryLabel}</span>
                                    )}
                                  </span>
                                </td>
                                <td className="px-3 py-1.5 text-right font-mono text-zinc-600">
                                  {row.kind === 'job' ? row.hours.toFixed(2) : '—'}
                                </td>
                                <td className={`px-3 py-1.5 text-right font-mono font-semibold ${row.categoryTone === 'amber' ? 'text-amber-700' : 'text-emerald-700'}`}>
                                  &euro;{row.amount.toFixed(2)}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                        {filteredCombinedStmtRows.length > 0 && (
                          <tfoot>
                            <tr className="border-t-2 border-zinc-300 bg-zinc-50 font-semibold">
                              <td colSpan={3} className="px-3 py-1.5 text-xs uppercase text-zinc-400 text-right">{t('total')}</td>
                              <td className="px-3 py-1.5 text-right font-mono text-zinc-700">
                                {filteredCombinedStmtRows.reduce((s, r) => s + (r.kind === 'job' ? r.hours : 0), 0).toFixed(2)}
                              </td>
                              <td className="px-3 py-1.5 text-right font-mono text-zinc-900">
                                &euro;{filteredCombinedStmtRows.reduce((s, r) => s + r.amount, 0).toFixed(2)}
                              </td>
                            </tr>
                          </tfoot>
                        )}
                      </table>
                    </div>
                  </div>
                )}

                {/* Section 1: Buildings Cleaning – Daily Breakdown */}
                {stmtViewMode === 'buildings' && (
                <div data-testid="stmt-buildings-section">
                  <h4 className="text-xs font-semibold text-zinc-500 uppercase mb-2">{t('buildingsCleaning')} <span className="text-[10px] font-normal text-zinc-400">({t('cleaningHistory')})</span></h4>
                  <div className="border border-zinc-200">
                    <table className="w-full text-xs"><thead><tr className="bg-zinc-50 border-b text-zinc-400"><th className="w-8"></th><th className="text-left px-3 py-1.5">{t('date')}</th><th className="text-center px-3 py-1.5">{t('cleanings')}</th><th className="text-right px-3 py-1.5">{t('amount')}</th></tr></thead>
                      <tbody>{(!sortedBuildingsCleaning || sortedBuildingsCleaning.length === 0) ? <tr><td colSpan={4} className="text-center py-4 text-zinc-400">{t('noData')}</td></tr> : sortedBuildingsCleaning.map((row, i) => {
                        const isExpanded = expandedDates[row.date];
                        return (<React.Fragment key={i}>
                          <tr className="border-b border-zinc-100 hover:bg-zinc-50/60 cursor-pointer transition-colors" onClick={() => setExpandedDates(prev => ({ ...prev, [row.date]: !prev[row.date] }))} data-testid={`bld-cleaning-row-${i}`}>
                            <td className="px-1.5 py-1.5 text-center text-zinc-400">{isExpanded ? <CaretUp size={12} /> : <CaretDown size={12} />}</td>
                            <td className="px-3 py-1.5 font-mono">{fmtDate(row.date)}</td>
                            <td className="px-3 py-1.5 text-center"><span className="inline-flex items-center justify-center w-5 h-5 text-[10px] font-bold border border-zinc-300 bg-zinc-100 text-zinc-600">{row.entry_count}</span></td>
                            <td className="px-3 py-1.5 text-right font-mono font-semibold text-amber-700">&euro;{row.amount?.toFixed(2)}</td>
                          </tr>
                          {isExpanded && row.buildings && row.buildings.map((b, bi) => (
                            <tr key={`${i}-${bi}`} className="bg-amber-50/40 border-b border-amber-100/50" data-testid={`bld-detail-${i}-${bi}`}>
                              <td></td>
                              <td className="px-3 py-1 pl-6 text-zinc-500">{b.building_name || b.building_id}</td>
                              <td></td>
                              <td className="px-3 py-1 text-right font-mono text-amber-600">&euro;{b.amount?.toFixed(2)}</td>
                            </tr>
                          ))}
                        </React.Fragment>);
                      })}</tbody>
                      {sortedBuildingsCleaning.length > 0 && <tfoot><tr className="border-t-2 border-zinc-300 bg-zinc-50 font-semibold"><td></td><td className="px-3 py-1.5 text-xs uppercase text-zinc-400">{t('total')}</td><td className="px-3 py-1.5 text-center text-xs text-zinc-500">{statementData.buildings_entry_count}</td><td className="px-3 py-1.5 text-right font-mono text-amber-700">&euro;{statementData.total_buildings?.toFixed(2)}</td></tr></tfoot>}
                    </table>
                  </div>
                </div>
                )}

                {/* Section 2: Job History Details (from cleaning_jobs) */}
                {stmtViewMode === 'jobs' && (
                <div data-testid="stmt-jobs-section">
                  <h4 className="text-xs font-semibold text-zinc-500 uppercase mb-2">{t('jobHistory')} <span className="text-[10px] font-normal text-zinc-400">({t('details')})</span></h4>
                  <div className="border border-zinc-200">
                    <table className="w-full text-xs"><thead><tr className="bg-emerald-50 border-b text-zinc-400"><th className="text-left px-3 py-1.5">{t('date')}</th><th className="text-left px-3 py-1.5">{t('client')}</th><th className="text-center px-3 py-1.5">{t('hoursWorked')}</th><th className="text-right px-3 py-1.5">{t('amount')}</th></tr></thead>
                      <tbody>{(!sortedJobHistoryDetails || sortedJobHistoryDetails.length === 0) ? <tr><td colSpan={4} className="text-center py-4 text-zinc-400">{t('noData')}</td></tr> : sortedJobHistoryDetails.map((j, ji) => (<tr key={ji} className="border-b border-zinc-100" data-testid={`stmt-job-row-${ji}`}><td className="px-3 py-1.5 font-mono">{fmtDate(j.date)}</td><td className="px-3 py-1.5">{j.client_name}</td><td className="px-3 py-1.5 text-center font-mono text-zinc-600">{(Number(j.hours_worked) || 0).toFixed(2)}</td><td className="px-3 py-1.5 text-right font-mono font-semibold text-emerald-700">&euro;{j.amount?.toFixed(2)}</td></tr>))}</tbody>
                      {sortedJobHistoryDetails.length > 0 && <tfoot><tr className="border-t-2 border-zinc-300 bg-zinc-50 font-semibold"><td colSpan={2} className="px-3 py-1.5 text-right text-xs uppercase text-zinc-400">{t('total')}</td><td className="px-3 py-1.5 text-center font-mono text-zinc-700">{(sortedJobHistoryDetails.reduce((s, x) => s + (Number(x.hours_worked) || 0), 0)).toFixed(2)}</td><td className="px-3 py-1.5 text-right font-mono text-emerald-700">&euro;{statementData.total_jobs?.toFixed(2)}</td></tr></tfoot>}
                    </table>
                  </div>
                </div>
                )}

                {/* Section 3: Payments — always visible */}
                <div data-testid="stmt-payments-section"><h4 className="text-xs font-semibold text-zinc-500 uppercase mb-2">{t('payments')}</h4>
                  <div className="border border-zinc-200">
                    <table className="w-full text-xs"><thead><tr className="bg-zinc-50 border-b text-zinc-400"><th className="text-left px-3 py-1.5">{t('date')}</th><th className="text-right px-3 py-1.5">{t('amount')}</th><th className="text-left px-3 py-1.5">{t('notes')}</th></tr></thead>
                      <tbody>{sortedClnrStmtPayments.length === 0 ? <tr><td colSpan={3} className="text-center py-4 text-zinc-400">{t('noData')}</td></tr> : sortedClnrStmtPayments.map(p => (<tr key={p.payment_id} className="border-b border-zinc-100" data-testid={`stmt-pay-row-${p.payment_id}`}><td className="px-3 py-1 font-mono">{fmtDate(p.date)}</td><td className="px-3 py-1 text-right font-mono text-emerald-700">&euro;{p.amount?.toFixed(2)}</td><td className="px-3 py-1 text-zinc-500">{p.notes || '-'}</td></tr>))}</tbody></table>
                  </div>
                </div>
              </div>
            )}
            </div>
            <DialogFooter className="flex-col sm:flex-row gap-2 items-stretch sm:items-center px-6 py-3 border-t border-zinc-200 bg-white">
              <div className="flex items-center gap-2 mr-auto">
                <label className="flex items-center gap-2 text-xs text-zinc-600 cursor-pointer select-none" data-testid="show-buildings-toggle">
                  <input type="checkbox" checked={showBuildingsInPdf} onChange={e => setShowBuildingsInPdf(e.target.checked)} className="accent-zinc-700 w-3.5 h-3.5" />
                  {t('showBuildingNames')}
                </label>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => { setStatementCleaner(null); setStatementData(null); }} className="rounded-none">{t('close')}</Button>
                {statementCleaner && (() => {
                  const buildParams = () => {
                    const p = { show_buildings: showBuildingsInPdf ? 'true' : 'false' };
                    if (stmtClnrFrom) p.date_from = stmtClnrFrom;
                    if (stmtClnrTo) p.date_to = stmtClnrTo;
                    if (stmtClnrClientFilter && stmtClnrClientFilter !== 'all') p.client_id = stmtClnrClientFilter;
                    p.sort_order = sortOrder;
                    return p;
                  };
                  const path = `/cleaning/cleaners/${statementCleaner.cleaner_id}/statement/pdf`;
                  return (
                    <>
                      <button onClick={() => openPdfInBrowser(path, buildParams())} className="inline-flex items-center justify-center rounded-none px-4 py-2 bg-zinc-900 text-white text-sm font-medium hover:bg-zinc-800" data-testid="view-pdf-btn"><Eye size={16} className="mr-1" /> {t('viewPdf') || 'View PDF'}</button>
                    </>
                  );
                })()}
              </div>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Phase 605 — Client Invoice Dialog removed. Cleaning Management is now
            a strict READ-ONLY mirror of the canonical /sales `sales_invoices`
            collection. Create / edit / delete happens exclusively in /sales. */}

        {/* Category Dialog */}
        <Dialog open={catDialog} onOpenChange={setCatDialog}>
          <DialogContent className="sm:max-w-sm" data-testid="category-dialog"><DialogHeader><DialogTitle>{editingCat ? t('edit') + ' ' + t('categories') : t('add') + ' ' + t('categories')}</DialogTitle></DialogHeader>
            <div className="space-y-3"><div><Label>{t('name')} (EN) *</Label><Input value={catForm.name} onChange={e => setCatForm(f => ({ ...f, name: e.target.value }))} className="rounded-none" data-testid="cat-name-input" /></div><div><Label>{t('name')} (EL)</Label><Input value={catForm.name_el} onChange={e => setCatForm(f => ({ ...f, name_el: e.target.value }))} className="rounded-none" /></div></div>
            <DialogFooter><Button variant="outline" onClick={() => setCatDialog(false)} className="rounded-none">{t('cancel')}</Button><Button onClick={saveCat} className="rounded-none" data-testid="save-cat-btn">{t('save')}</Button></DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmations */}
        <AlertDialog open={!!deletingCleaner} onOpenChange={() => setDeletingCleaner(null)}><AlertDialogContent><AlertDialogHeader><AlertDialogTitle>{t('confirmDelete')}</AlertDialogTitle><AlertDialogDescription>{t('deleteConfirmation')}</AlertDialogDescription></AlertDialogHeader><AlertDialogFooter><AlertDialogCancel className="rounded-none">{t('cancel')}</AlertDialogCancel><AlertDialogAction onClick={deleteCleaner} className="bg-red-600 rounded-none" data-testid="confirm-delete-cleaner">{t('delete')}</AlertDialogAction></AlertDialogFooter></AlertDialogContent></AlertDialog>
        {/* Phase 607 — Mark Inactive / Reactivate confirmation (replaces the
            silently-blocked window.confirm). Capacitor mobile wrapper does
            not surface native confirm dialogs at all, so we must use an
            inline AlertDialog component for any destructive-ish action. */}
        <AlertDialog open={!!pendingStatusCleaner} onOpenChange={(o) => { if (!o) setPendingStatusCleaner(null); }}>
          <AlertDialogContent data-testid="confirm-cleaner-status-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>
                {(pendingStatusCleaner?.status || 'active') === 'inactive'
                  ? (t('reactivate') || 'Reactivate')
                  : (t('markInactive') || 'Mark Inactive')}
                {pendingStatusCleaner ? ` — ${pendingStatusCleaner.name}` : ''}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {(pendingStatusCleaner?.status || 'active') === 'inactive'
                  ? (t('confirmReactivateCleaner') || 'Reactivate this cleaner? They will reappear in the Active list.')
                  : (t('confirmDeactivateCleaner') || 'Mark this cleaner as inactive? Their full history is preserved and they will disappear from the Active list.')}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="rounded-none" disabled={cleanerStatusBusy} data-testid="confirm-cleaner-status-cancel">{t('cancel')}</AlertDialogCancel>
              <AlertDialogAction
                onClick={(e) => { e.preventDefault(); confirmToggleCleanerStatus(); }}
                disabled={cleanerStatusBusy}
                className={`rounded-none ${(pendingStatusCleaner?.status || 'active') === 'inactive' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-amber-600 hover:bg-amber-700'}`}
                data-testid="confirm-cleaner-status-action"
              >
                {cleanerStatusBusy
                  ? (t('saving') || 'Saving…')
                  : (((pendingStatusCleaner?.status || 'active') === 'inactive')
                      ? (t('reactivate') || 'Reactivate')
                      : (t('markInactive') || 'Mark Inactive'))}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
        <AlertDialog open={!!deletingClient} onOpenChange={() => setDeletingClient(null)}><AlertDialogContent><AlertDialogHeader><AlertDialogTitle>{t('confirmDelete')}</AlertDialogTitle><AlertDialogDescription>{t('deleteConfirmation')}</AlertDialogDescription></AlertDialogHeader><AlertDialogFooter><AlertDialogCancel className="rounded-none">{t('cancel')}</AlertDialogCancel><AlertDialogAction onClick={deleteClient} className="bg-red-600 rounded-none" data-testid="confirm-delete-client">{t('delete')}</AlertDialogAction></AlertDialogFooter></AlertDialogContent></AlertDialog>
        <AlertDialog open={!!deletingJob} onOpenChange={() => setDeletingJob(null)}><AlertDialogContent><AlertDialogHeader><AlertDialogTitle>{t('confirmDelete')}</AlertDialogTitle><AlertDialogDescription>{t('deleteConfirmation')}</AlertDialogDescription></AlertDialogHeader><AlertDialogFooter><AlertDialogCancel className="rounded-none">{t('cancel')}</AlertDialogCancel><AlertDialogAction onClick={deleteJob} className="bg-red-600 rounded-none" data-testid="confirm-delete-job">{t('delete')}</AlertDialogAction></AlertDialogFooter></AlertDialogContent></AlertDialog>
        <AlertDialog open={!!deletingCat} onOpenChange={() => setDeletingCat(null)}><AlertDialogContent><AlertDialogHeader><AlertDialogTitle>{t('confirmDelete')}</AlertDialogTitle><AlertDialogDescription>{t('deleteConfirmation')}</AlertDialogDescription></AlertDialogHeader><AlertDialogFooter><AlertDialogCancel className="rounded-none">{t('cancel')}</AlertDialogCancel><AlertDialogAction onClick={deleteCat} className="bg-red-600 rounded-none" data-testid="confirm-delete-cat">{t('delete')}</AlertDialogAction></AlertDialogFooter></AlertDialogContent></AlertDialog>

        {/* Phase 263 — Bulk delete confirmation. */}
        <AlertDialog open={!!bulkDeleteConfirm} onOpenChange={() => setBulkDeleteConfirm(null)}>
          <AlertDialogContent data-testid="bulk-delete-confirm">
            <AlertDialogHeader>
              <AlertDialogTitle>{t('confirmDelete')}</AlertDialogTitle>
              <AlertDialogDescription>
                {bulkDeleteConfirm
                  ? `${t('areYouSureDeleteX', { count: bulkDeleteConfirm.ids.length }) || `Are you sure you want to delete ${bulkDeleteConfirm.ids.length} items?`}`
                  : ''}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="rounded-none">{t('cancel')}</AlertDialogCancel>
              <AlertDialogAction onClick={() => performBulkDelete(false)} className="bg-red-600 rounded-none" data-testid="confirm-bulk-delete">
                {t('delete')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Phase 263 — Bulk delete result (when some records were blocked
            due to linked jobs). The user can either close or force-delete. */}
        <AlertDialog open={!!bulkDeleteResult} onOpenChange={() => setBulkDeleteResult(null)}>
          <AlertDialogContent data-testid="bulk-delete-result">
            <AlertDialogHeader>
              <AlertDialogTitle>{t('partiallyDeleted') || 'Some records could not be deleted'}</AlertDialogTitle>
              <AlertDialogDescription>
                {bulkDeleteResult ? (
                  <span>
                    {`${bulkDeleteResult.deleted.length} ${t('deleted').toLowerCase()}. ${bulkDeleteResult.blocked.length} ${t('linkedToJobs') || 'linked to jobs/history'}:`}
                    <ul className="mt-2 text-xs space-y-1 max-h-40 overflow-y-auto">
                      {bulkDeleteResult.blocked.map((b) => (
                        <li key={b.id} className="text-zinc-700">
                          • <strong>{b.name}</strong> — {b.linked_jobs} {t('jobs')}
                        </li>
                      ))}
                    </ul>
                  </span>
                ) : ''}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="rounded-none" onClick={() => setBulkDeleteResult(null)}>{t('close') || 'Close'}</AlertDialogCancel>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
}


// Phase 637 — Minimal class-based ErrorBoundary so a runtime error
// inside the (very large) Cleaning Management tree shows an inline
// error block instead of white-screening the whole page. The user can
// recover via a "Reload" button without losing other open tabs.
class CleaningManagementErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error('[CleaningManagement] crash:', error, info);
  }
  render() {
    if (this.state.error) {
      const msg = (this.state.error && (this.state.error.message || String(this.state.error))) || 'Unknown error';
      return (
        <Layout>
          <div className="p-6">
            <div className="border border-red-200 bg-red-50 px-4 py-4" data-testid="cleaning-management-error-boundary">
              <div className="text-sm font-semibold text-red-700 mb-1">Cleaning Management hit a runtime error.</div>
              <div className="text-xs font-mono text-red-700 break-words mb-2">{msg}</div>
              <div className="text-xs text-zinc-600 mb-3">Other parts of the app are still working. Reload this page once the issue is reported.</div>
              <button
                type="button"
                onClick={() => { this.setState({ error: null }); window.location.reload(); }}
                className="rounded-none h-7 px-3 text-xs border border-red-300 bg-white text-red-700 hover:bg-red-100"
                data-testid="cleaning-management-error-reload"
              >Reload</button>
            </div>
          </div>
        </Layout>
      );
    }
    return this.props.children;
  }
}

export default function CleaningManagement() {
  return (
    <CleaningManagementErrorBoundary>
      <CleaningManagementInner />
    </CleaningManagementErrorBoundary>
  );
}
